import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { getApiEndpoint } from "../../config/config";
import ScalesPreview from "../shared/ScalesPreview/ScalesPreview";
import "./RepDetailsPage.css";
import PageContent from "../shared/PageContent/PageContent";
import EditRepInfoModal from "./EditRepInfoModal";
import EditScalesModal from "./EditScalesModal";
import EditProjectedRevenueModal from "./EditProjectedRevenueModal";
import DeactivateRepModal from "./DeactivateRepModal";
import RepDealTab from "./RepDealTab";
import {
  Modal,
  Stack,
  Group,
  Text,
  PinInput,
  Button,
  Alert,
  Divider,
  Loader,
  Title,
  Select,
} from "@mantine/core";
import {
  IconLock,
  IconShieldCheck,
  IconInfoCircle,
  IconAlertCircle,
  IconMail,
  IconPhone,
  IconCalendar,
  IconBrandInstagram,
  IconBrandLinkedin,
  IconUser,
  IconEdit,
  IconCurrencyDollar,
  IconClock,
  IconTrophy,
  IconNetwork,
  IconHeart,
  IconActivity,
  IconTargetArrow,
  IconVideo,
  IconFileText,
  IconUsers,
  IconChecklist,
} from "@tabler/icons-react";
import "../Organization/OrganizationPage.css";
import {
  DEFAULT_PIN_UNLOCK_DURATION,
  PIN_UNLOCK_DURATIONS,
  clearPinUnlockSession,
  getActivePin,
  isPinUnlockSessionActive,
  startPinUnlockSession,
} from "../../utils/pinUnlockSession";

function RepDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  // `?tab=` lets other pages (e.g. the add-deal flow) return to a specific tab.
  const [searchParams] = useSearchParams();
  const { user, getVerifiedJWT } = useAuth();
  const [rep, setRep] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editScaleType, setEditScaleType] = useState(null);
  const [isDeactivateModalOpen, setIsDeactivateModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [profileImageUrl, setProfileImageUrl] = useState(null);
  const [reps, setReps] = useState([]);
  const [repTypes, setRepTypes] = useState([]);
  const [recruitingSourceTypes, setRecruitingSourceTypes] = useState([]);
  const [isPinModalOpen, setIsPinModalOpen] = useState(false);
  const [pin, setPin] = useState("");
  const [pinVerifying, setPinVerifying] = useState(false);
  const [pinError, setPinError] = useState(null);
  const [pinForEdit, setPinForEdit] = useState(false);
  const [unlockDuration, setUnlockDuration] = useState(
    String(DEFAULT_PIN_UNLOCK_DURATION)
  );
  const [compensation, setCompensation] = useState(null);
  const [showCompensation, setShowCompensation] = useState(false);
  const [isEditProjectedRevenueModalOpen, setIsEditProjectedRevenueModalOpen] =
    useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [didEditBonusTiers, setDidEditBonusTiers] = useState(false);
  const [didEditCommissionScale, setDidEditCommissionScale] = useState(false);
  const [didEditManagerPayScale, setDidEditManagerPayScale] = useState(false);
  const [didEditPeerOverrides, setDidEditPeerOverrides] = useState(false);
  const [showSaveConfirmModal, setShowSaveConfirmModal] = useState(false);
  const [showCancelConfirmModal, setShowCancelConfirmModal] = useState(false);
  const [managerPayType, setManagerPayType] = useState("override"); // "override" or "md"
  // "details", "history", "documents", or "deal"
  const [activeTab, setActiveTab] = useState(
    () => searchParams.get("tab") || "details"
  );
  const [history, setHistory] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);

  // Documents tab state
  const [repDocuments, setRepDocuments] = useState(null);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [documentsError, setDocumentsError] = useState(null);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [uploadLabel, setUploadLabel] = useState("");
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [isManagerPayTypeSelectionOpen, setIsManagerPayTypeSelectionOpen] =
    useState(false);
  const [isEditGroupsModalOpen, setIsEditGroupsModalOpen] = useState(false);
  const [availableGroups, setAvailableGroups] = useState([]);
  const [repGroups, setRepGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [savingGroups, setSavingGroups] = useState(false);

  // Default scale templates
  const [commissionTemplates, setCommissionTemplates] = useState([]);
  const [overrideTemplates, setOverrideTemplates] = useState([]);
  const [marketingTemplates, setMarketingTemplates] = useState([]);
  const [selectedCommissionTemplate, setSelectedCommissionTemplate] =
    useState("");
  const [selectedOverrideTemplate, setSelectedOverrideTemplate] = useState("");
  const [selectedMarketingTemplate, setSelectedMarketingTemplate] =
    useState("");

  const [editFormData, setEditFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    instagram: "",
    linkedin: "",
    birthday: "",
    saleStartDate: "",
    saleEndDate: "",
    recruitingSource: "",
    isManager: false,
    manager: "",
    recruitedBy: "",
    revenueGoal: "",
    projectedRevenue: "",
    commissionScaleLines: [],
    overrideScaleLines: [],
    mdScaleLines: [],
    productionBonuses: [],
    peerOverrides: [],
  });

  const resetCompensationDirtyFlags = () => {
    setDidEditBonusTiers(false);
    setDidEditCommissionScale(false);
    setDidEditManagerPayScale(false);
    setDidEditPeerOverrides(false);
  };

  const markScaleTypeEdited = scaleType => {
    if (scaleType === "commissionScaleLines") {
      setDidEditCommissionScale(true);
      return;
    }

    if (scaleType === "overrideScaleLines" || scaleType === "mdScaleLines") {
      setDidEditManagerPayScale(true);
    }
  };

  const handleEditClick = () => {
    if (rep) {
      // Check if compensation data is loaded
      if (!showCompensation) {
        if (isPinUnlockSessionActive()) {
          const activePin = getActivePin();
          if (activePin) {
            fetchCompensationDetails(activePin, true, true);
            return;
          }
        }

        // Need to prompt for PIN first
        setPinForEdit(true);
        setIsPinModalOpen(true);
        return;
      }

      // Compensation is loaded, proceed with edit
      // Determine initial manager pay type based on existing data
      const hasOverride =
        compensation?.override_scale_lines &&
        compensation.override_scale_lines.length > 0;
      const hasMD =
        compensation?.md_scale_lines && compensation.md_scale_lines.length > 0;

      setManagerPayType(hasOverride ? "override" : hasMD ? "md" : "override");

      setEditFormData({
        firstName: rep.first_name || "",
        lastName: rep.last_name || "",
        email: rep.email || "",
        phoneNumber: rep.phone_number || "",
        instagram: rep.instagram || "",
        linkedin: rep.linked_in || "",
        birthday: rep.birthday ? rep.birthday.split("T")[0] : "",
        saleStartDate: rep.sale_start_date
          ? rep.sale_start_date.split("T")[0]
          : "",
        saleEndDate: rep.sale_end_date ? rep.sale_end_date.split("T")[0] : "",
        recruitingSource: rep.recruiting_source_id
          ? String(rep.recruiting_source_id)
          : "",
        isManager: rep.is_manager || false,
        manager: rep.manager?.user?.rep_id
          ? String(rep.manager.user.rep_id)
          : "",
        recruitedBy: rep.recruiter?.user?.rep_id
          ? String(rep.recruiter.user.rep_id)
          : "",
        revenueGoal: rep.revenue_goal ? rep.revenue_goal.toLocaleString() : "",
        projectedRevenue:
          compensation?.projected_revenue !== null &&
          compensation?.projected_revenue !== undefined
            ? compensation.projected_revenue.toLocaleString()
            : "",
        commissionScaleLines:
          compensation?.commission_scale_lines &&
          compensation.commission_scale_lines.length > 0
            ? compensation.commission_scale_lines.map((line, index) => ({
                id: index + 1,
                revenue_amount: line.revenue_amount ?? "",
                percentage: line.percentage ?? "",
              }))
            : [{ id: 1, revenue_amount: "", percentage: "" }],
        overrideScaleLines:
          compensation?.override_scale_lines &&
          compensation.override_scale_lines.length > 0
            ? compensation.override_scale_lines.map((line, index) => ({
                id: index + 1,
                revenue_amount: line.revenue_amount ?? "",
                percentage: line.percentage ?? "",
              }))
            : [],
        mdScaleLines:
          compensation?.md_scale_lines && compensation.md_scale_lines.length > 0
            ? compensation.md_scale_lines.map((line, index) => ({
                id: index + 1,
                revenue_amount: line.revenue_amount ?? "",
                percentage: line.percentage ?? "",
              }))
            : [],
        productionBonuses:
          compensation?.production_bonuses &&
          compensation.production_bonuses.length > 0
            ? compensation.production_bonuses.map((b, index) => ({
                id: index + 1,
                revenue_amount: b.revenue_amount ?? "",
                bonus_amount: b.bonus_amount ?? "",
              }))
            : [],
        peerOverrides: compensation?.peer_overrides
          ? compensation.peer_overrides
              .filter(o => o.role === "grantor")
              .map((o, index) => ({
                id: index + 1,
                grantor_user_season_id:
                  o.grantor_user_season_id != null
                    ? String(o.grantor_user_season_id)
                    : "",
                recipient_user_season_id:
                  o.recipient_user_season_id != null
                    ? String(o.recipient_user_season_id)
                    : "",
                recipient_name: o.recipient_name || "",
                label: o.label || "",
                scale_lines:
                  o.scale_lines && o.scale_lines.length > 0
                    ? o.scale_lines.map((l, li) => ({
                        id: li + 1,
                        revenue_amount: l.revenue_amount ?? "",
                        percentage: l.percentage ?? "",
                        condition_type:
                          l.condition_type || o.condition_type || "REVENUE_GTE",
                      }))
                    : [
                        {
                          id: 1,
                          revenue_amount: "",
                          percentage: "",
                          condition_type: "REVENUE_GTE",
                        },
                      ],
              }))
          : [],
      });
      resetCompensationDirtyFlags();
      setIsEditing(true);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    resetCompensationDirtyFlags();
    setEditFormData({
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
      instagram: "",
      linkedin: "",
      birthday: "",
      saleStartDate: "",
      saleEndDate: "",
      recruitingSource: "",
      isManager: false,
      manager: "",
      recruitedBy: "",
      revenueGoal: "",
      projectedRevenue: "",
      commissionScaleLines: [],
      overrideScaleLines: [],
      mdScaleLines: [],
    });
  };

  const handleInputChange = (field, value) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handlePhoneChange = value => {
    const cleaned = value.replace(/\D/g, "");
    let formattedValue = cleaned;

    if (cleaned.length >= 6) {
      formattedValue = `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
    } else if (cleaned.length >= 3) {
      formattedValue = `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    }

    setEditFormData(prev => ({
      ...prev,
      phoneNumber: formattedValue,
    }));
  };

  const handleRevenueGoalChange = value => {
    // Remove all non-digit characters
    const cleaned = value.replace(/\D/g, "");

    // Format with commas
    const formatted = cleaned ? parseInt(cleaned).toLocaleString() : "";

    setEditFormData(prev => ({
      ...prev,
      revenueGoal: formatted,
    }));
  };

  // Scale line management functions
  const handleScaleLineChange = (scaleType, lineId, field, value) => {
    markScaleTypeEdited(scaleType);
    setEditFormData(prev => ({
      ...prev,
      [scaleType]: prev[scaleType].map(line =>
        line.id === lineId ? { ...line, [field]: value } : line
      ),
    }));
  };

  const addScaleLine = scaleType => {
    markScaleTypeEdited(scaleType);
    setEditFormData(prev => {
      const lines = prev[scaleType];
      const newId =
        lines.length > 0 ? Math.max(...lines.map(l => l.id)) + 1 : 1;
      return {
        ...prev,
        [scaleType]: [
          ...lines,
          { id: newId, revenue_amount: "", percentage: "" },
        ],
      };
    });
  };

  const removeScaleLine = (scaleType, lineId) => {
    markScaleTypeEdited(scaleType);
    setEditFormData(prev => ({
      ...prev,
      [scaleType]: prev[scaleType].filter(line => line.id !== lineId),
    }));
  };

  const safeParseInt = value => {
    if (!value || value === "") return undefined;
    const parsed = parseInt(value);
    return isNaN(parsed) ? undefined : parsed;
  };

  const handleSaveEdit = async () => {
    try {
      setSaving(true);
      const jwt = await getVerifiedJWT();
      if (!jwt) {
        setError("Authentication failed");
        return;
      }

      const cleanPhoneNumber = editFormData.phoneNumber.replace(/\D/g, "");
      const cleanProjectedRevenue = editFormData.projectedRevenue
        ? editFormData.projectedRevenue.replace(/,/g, "").trim()
        : "";
      const cleanRevenueGoal = editFormData.revenueGoal
        ? editFormData.revenueGoal.replace(/,/g, "").trim()
        : "";

      const parsedProjectedRevenue = cleanProjectedRevenue
        ? parseFloat(cleanProjectedRevenue)
        : null;
      const parsedRevenueGoal = cleanRevenueGoal
        ? parseFloat(cleanRevenueGoal)
        : null;

      // Validate parsed values
      if (parsedProjectedRevenue !== null && isNaN(parsedProjectedRevenue)) {
        setError(
          "Invalid projected revenue value. Please enter a valid number."
        );
        setSaving(false);
        return;
      }
      if (parsedRevenueGoal !== null && isNaN(parsedRevenueGoal)) {
        setError("Invalid revenue goal value. Please enter a valid number.");
        setSaving(false);
        return;
      }

      // Validate peer overrides — catch incomplete rows before silently dropping them
      const incompletePeerOverrides = editFormData.peerOverrides.filter(
        o =>
          // Row has some data filled in (scale lines or label) but is missing the receiver rep
          (o.label !== "" ||
            o.scale_lines.some(
              l => l.revenue_amount !== "" || l.percentage !== ""
            )) &&
          o.recipient_user_season_id === ""
      );
      if (incompletePeerOverrides.length > 0) {
        setError(
          `One or more Custom Peer Override rows are missing a "Receiver" rep. Please select a rep from the Receiver dropdown for each override row, or remove the incomplete row.`
        );
        setSaving(false);
        return;
      }

      const peerOverridesWithMissingScaleLines =
        editFormData.peerOverrides.filter(
          o =>
            o.recipient_user_season_id !== "" &&
            o.scale_lines.every(l => l.percentage === "")
        );
      if (peerOverridesWithMissingScaleLines.length > 0) {
        setError(
          `One or more Custom Peer Override rows are missing scale line values. Please add at least one revenue/percentage row for each override, or remove the incomplete row.`
        );
        setSaving(false);
        return;
      }

      // Process commission scale lines
      // Use !== "" so that revenue_amount === 0 (falsy) is not incorrectly dropped
      const validCommissionLines = editFormData.commissionScaleLines.filter(
        line => line.revenue_amount !== "" && line.percentage !== ""
      );
      const processedCommissionScale = validCommissionLines.map(line => ({
        revenue: parseFloat(line.revenue_amount),
        percentage: parseFloat(line.percentage),
      }));

      // Process override scale lines - only if override is selected
      const validOverrideLines = editFormData.overrideScaleLines.filter(
        line => line.revenue_amount !== "" && line.percentage !== ""
      );
      const processedOverrideScale =
        managerPayType === "override"
          ? validOverrideLines.map(line => ({
              revenue: parseFloat(line.revenue_amount),
              percentage: parseFloat(line.percentage),
            }))
          : [];

      // Process marketing deal scale lines - only if md is selected
      const validMdLines = editFormData.mdScaleLines.filter(
        line => line.revenue_amount !== "" && line.percentage !== ""
      );
      const processedMdScale =
        managerPayType === "md"
          ? validMdLines.map(line => ({
              revenue: parseFloat(line.revenue_amount),
              percentage: parseFloat(line.percentage),
            }))
          : [];

      const apiData = {
        first_name: editFormData.firstName || undefined,
        last_name: editFormData.lastName || undefined,
        email: editFormData.email || undefined,
        phone_number: cleanPhoneNumber || undefined,
        instagram_username: editFormData.instagram || undefined,
        linkedin_profile: editFormData.linkedin || undefined,
        birthday: editFormData.birthday || undefined,
        sale_start_date: editFormData.saleStartDate || undefined,
        sale_end_date: editFormData.saleEndDate || undefined,
        is_manager: editFormData.isManager,
        manager: safeParseInt(editFormData.manager),
        recruited_by: safeParseInt(editFormData.recruitedBy),
        recruiting_source_id: safeParseInt(editFormData.recruitingSource),
        revenue_goal: parsedRevenueGoal,
        projected_revenue: parsedProjectedRevenue,
        ...(didEditCommissionScale
          ? {
              commission_scale:
                processedCommissionScale.length > 0
                  ? processedCommissionScale
                  : undefined,
            }
          : {}),
        // Manager pay scales are only sent when edited.
        // If edited, we still explicitly clear the opposite type.
        ...(didEditManagerPayScale
          ? {
              override_scale:
                managerPayType === "override"
                  ? processedOverrideScale.length > 0
                    ? processedOverrideScale
                    : []
                  : [],
              marketing_deal_scale:
                managerPayType === "md"
                  ? processedMdScale.length > 0
                    ? processedMdScale
                    : []
                  : [],
            }
          : {}),
        ...(showCompensation
          ? {
              // Production bonuses — only send when user actually edited bonus tiers.
              // This prevents accidental wipes when editing unrelated rep fields.
              ...(didEditBonusTiers
                ? {
                    production_bonuses: editFormData.productionBonuses
                      .filter(
                        b => b.revenue_amount !== "" && b.bonus_amount !== ""
                      )
                      .map(b => ({
                        revenue_amount: parseFloat(b.revenue_amount),
                        bonus_amount: parseFloat(b.bonus_amount),
                      })),
                  }
                : {}),
              ...(didEditPeerOverrides
                ? {
                    // Peer overrides — only rows that have a receiver and at least one valid scale line
                    peer_overrides: editFormData.peerOverrides
                      .filter(
                        o =>
                          o.recipient_user_season_id !== "" &&
                          o.scale_lines.some(l => l.percentage !== "")
                      )
                      .map(o => ({
                        grantor_user_season_id:
                          o.grantor_user_season_id !== ""
                            ? parseInt(o.grantor_user_season_id)
                            : undefined,
                        recipient_user_season_id: parseInt(
                          o.recipient_user_season_id
                        ),
                        label: o.label || undefined,
                        condition_type:
                          o.scale_lines[0]?.condition_type || "REVENUE_GTE",
                        condition_value_1:
                          o.scale_lines[0]?.revenue_amount !== ""
                            ? parseFloat(o.scale_lines[0]?.revenue_amount)
                            : undefined,
                        condition_value_2: undefined,
                        scale_lines: o.scale_lines
                          .filter(l => l.percentage !== "")
                          .map(l => ({
                            revenue_amount:
                              l.revenue_amount !== ""
                                ? parseFloat(l.revenue_amount)
                                : 0,
                            percentage: parseFloat(l.percentage),
                          })),
                      })),
                  }
                : {}),
            }
          : {}),
      };

      const response = await fetch(getApiEndpoint(`/v1/rep/details/${id}`), {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify(apiData),
      });

      if (response.ok) {
        await fetchRepDetails(true); // Skip loading state to prevent flicker
        // Auto-refresh compensation data if it was visible
        if (showCompensation && pin) {
          await fetchCompensationDetails(pin, false);
        }
        resetCompensationDirtyFlags();
        setIsEditing(false);
        setError(null);
      } else {
        const data = await response.json();
        setError(
          data.message ||
            JSON.stringify(data) ||
            "Failed to update rep information"
        );
      }
    } catch (err) {
      setError("Failed to update rep information");
    } finally {
      setSaving(false);
    }
  };

  // Fetch basic rep details from API (no PIN required)
  const fetchRepDetails = async (skipLoadingState = false) => {
    try {
      if (!skipLoadingState) {
        setLoading(true);
      }
      const jwt = await getVerifiedJWT();

      if (!jwt) {
        setError("Failed to fetch rep details");
        return;
      }

      const response = await fetch(getApiEndpoint(`/v1/rep/details/${id}`), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        const data = result.data;
        setRep(data);
        setProfileImageUrl(data.profile_image_url || null);
        setReps(data.manager_reps || []);
        setRepTypes(data.rep_types || []);
        setRecruitingSourceTypes(data.recruiting_source_types || []);
        setError(null);
      } else {
        setError("Failed to fetch rep details");
      }
    } catch (err) {
      setError("Failed to fetch rep details");
    } finally {
      setLoading(false);
    }
  };

  // Fetch compensation details with PIN
  const fetchCompensationDetails = async (
    pinValue,
    shouldEnterEditMode = false,
    openPinModalOnFailure = true
  ) => {
    try {
      setPinVerifying(true);
      setPinError(null);
      const jwt = await getVerifiedJWT();

      if (!jwt) {
        setPinError("Authentication failed");
        return;
      }

      const response = await fetch(
        getApiEndpoint(`/v1/rep/${id}/compensation`),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
          body: JSON.stringify({
            pin: parseInt(pinValue),
          }),
        }
      );

      if (response.ok) {
        const result = await response.json();
        startPinUnlockSession({
          pin: pinValue,
          durationMinutes: Number(unlockDuration),
        });
        setCompensation(result.data);
        setShowCompensation(true);
        setIsPinModalOpen(false);
        setPinForEdit(false);
        // Don't clear PIN - keep it for re-fetching after save
        // setPin("");

        // Always update manager pay type based on the data
        const hasOverride =
          result.data?.override_scale_lines &&
          result.data.override_scale_lines.length > 0;
        const hasMD =
          result.data?.md_scale_lines && result.data.md_scale_lines.length > 0;

        setManagerPayType(hasOverride ? "override" : hasMD ? "md" : "override");

        // If called from edit button, proceed to edit mode
        if (shouldEnterEditMode && rep) {
          // Set manager pay type based on data
          const hasOverride =
            result.data?.override_scale_lines &&
            result.data.override_scale_lines.length > 0;
          const hasMD =
            result.data?.md_scale_lines &&
            result.data.md_scale_lines.length > 0;

          setManagerPayType(
            hasOverride ? "override" : hasMD ? "md" : "override"
          );

          setTimeout(() => {
            setEditFormData({
              firstName: rep.first_name || "",
              lastName: rep.last_name || "",
              email: rep.email || "",
              phoneNumber: rep.phone_number || "",
              instagram: rep.instagram || "",
              linkedin: rep.linked_in || "",
              birthday: rep.birthday ? rep.birthday.split("T")[0] : "",
              saleStartDate: rep.sale_start_date
                ? rep.sale_start_date.split("T")[0]
                : "",
              saleEndDate: rep.sale_end_date
                ? rep.sale_end_date.split("T")[0]
                : "",
              recruitingSource: rep.recruiting_source_id
                ? String(rep.recruiting_source_id)
                : "",
              isManager: rep.is_manager || false,
              manager: rep.manager?.user?.rep_id
                ? String(rep.manager.user.rep_id)
                : "",
              recruitedBy: rep.recruiter?.user?.rep_id
                ? String(rep.recruiter.user.rep_id)
                : "",
              revenueGoal: rep.revenue_goal
                ? rep.revenue_goal.toLocaleString()
                : "",
              projectedRevenue:
                result.data?.projected_revenue !== null &&
                result.data?.projected_revenue !== undefined
                  ? result.data.projected_revenue.toLocaleString()
                  : "",
              commissionScaleLines:
                result.data?.commission_scale_lines &&
                result.data.commission_scale_lines.length > 0
                  ? result.data.commission_scale_lines.map((line, index) => ({
                      id: index + 1,
                      revenue_amount: line.revenue_amount ?? "",
                      percentage: line.percentage ?? "",
                    }))
                  : [{ id: 1, revenue_amount: "", percentage: "" }],
              overrideScaleLines:
                result.data?.override_scale_lines &&
                result.data.override_scale_lines.length > 0
                  ? result.data.override_scale_lines.map((line, index) => ({
                      id: index + 1,
                      revenue_amount: line.revenue_amount ?? "",
                      percentage: line.percentage ?? "",
                    }))
                  : [],
              mdScaleLines:
                result.data?.md_scale_lines &&
                result.data.md_scale_lines.length > 0
                  ? result.data.md_scale_lines.map((line, index) => ({
                      id: index + 1,
                      revenue_amount: line.revenue_amount ?? "",
                      percentage: line.percentage ?? "",
                    }))
                  : [],
              productionBonuses:
                result.data?.production_bonuses &&
                result.data.production_bonuses.length > 0
                  ? result.data.production_bonuses.map((b, index) => ({
                      id: index + 1,
                      revenue_amount: b.revenue_amount ?? "",
                      bonus_amount: b.bonus_amount ?? "",
                    }))
                  : [],
              // peer_overrides in edit mode: only show grantor rows so manager can edit them
              peerOverrides: result.data?.peer_overrides
                ? result.data.peer_overrides
                    .filter(o => o.role === "grantor")
                    .map((o, index) => ({
                      id: index + 1,
                      grantor_user_season_id:
                        o.grantor_user_season_id != null
                          ? String(o.grantor_user_season_id)
                          : "",
                      recipient_user_season_id:
                        o.recipient_user_season_id != null
                          ? String(o.recipient_user_season_id)
                          : "",
                      recipient_name: o.recipient_name || "",
                      label: o.label || "",
                      scale_lines:
                        o.scale_lines && o.scale_lines.length > 0
                          ? o.scale_lines.map((l, li) => ({
                              id: li + 1,
                              revenue_amount: l.revenue_amount ?? "",
                              percentage: l.percentage ?? "",
                              condition_type:
                                l.condition_type ||
                                o.condition_type ||
                                "REVENUE_GTE",
                            }))
                          : [
                              {
                                id: 1,
                                revenue_amount: "",
                                percentage: "",
                                condition_type: "REVENUE_GTE",
                              },
                            ],
                    }))
                : [],
            });
            resetCompensationDirtyFlags();
            setIsEditing(true);
          }, 100);
        }
      } else {
        const data = await response.json();
        clearPinUnlockSession();
        setPinError(data.message || "Invalid PIN. Please try again.");
        setPin("");
        if (openPinModalOnFailure) {
          setIsPinModalOpen(true);
        }
      }
    } catch (err) {
      clearPinUnlockSession();
      setPinError("Failed to verify PIN. Please try again.");
      setPin("");
      if (openPinModalOnFailure) {
        setIsPinModalOpen(true);
      }
    } finally {
      setPinVerifying(false);
    }
  };

  const handleRepUpdate = () => {
    fetchRepDetails();
    setCompensation(null);
    setShowCompensation(false);
  };

  const handleViewCompensation = () => {
    if (isPinUnlockSessionActive()) {
      const activePin = getActivePin();
      if (activePin) {
        fetchCompensationDetails(activePin, false, true);
        return;
      }
    }

    setIsPinModalOpen(true);
  };

  const handlePinComplete = value => {
    setPin(value);
    if (value.length === 4) {
      setTimeout(() => {
        fetchCompensationDetails(value, pinForEdit);
      }, 300);
    }
  };

  const handleClosePinModal = () => {
    setIsPinModalOpen(false);
    setPin("");
    setPinError(null);
    setPinForEdit(false);
    setUnlockDuration(String(DEFAULT_PIN_UNLOCK_DURATION));
  };

  useEffect(() => {
    if (!showCompensation) return undefined;

    const interval = setInterval(() => {
      if (!isPinUnlockSessionActive()) {
        setShowCompensation(false);
        setCompensation(null);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [showCompensation]);

  const getInitials = () => {
    if (rep?.first_name && rep?.last_name) {
      return `${rep.first_name.charAt(0)}${rep.last_name.charAt(0)}`.toUpperCase();
    } else if (rep?.email) {
      return rep.email.charAt(0).toUpperCase();
    }
    return "R";
  };

  const formatDate = dateString => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatMonthDay = dateString => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    const day = date.getDate();
    const mod10 = day % 10;
    const mod100 = day % 100;
    const suffix =
      mod10 === 1 && mod100 !== 11
        ? "st"
        : mod10 === 2 && mod100 !== 12
          ? "nd"
          : mod10 === 3 && mod100 !== 13
            ? "rd"
            : "th";
    const month = date.toLocaleDateString("en-US", { month: "long" });
    return `${month} ${day}${suffix}`;
  };

  const formatPhoneNumber = phoneNumber => {
    if (!phoneNumber) return "N/A";
    const cleaned = phoneNumber.replace(/\D/g, "");
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    return phoneNumber;
  };

  const formatHistoryDate = value => {
    if (!value) return "Unknown date";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "Unknown date";
    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  const getHistoryAccent = source => {
    switch (source) {
      case "recruiting":
        return { bg: "#eff6ff", color: "#2563eb", label: "Recruiting" };
      case "esignature":
        return { bg: "#f5f3ff", color: "#7c3aed", label: "E-Signature" };
      case "team_event":
        return { bg: "#ecfdf5", color: "#059669", label: "Team Event" };
      case "manager_meeting":
        return {
          bg: "#eef2ff",
          color: "#4f46e5",
          label: "Manager Meeting",
        };
      case "attendance":
        return { bg: "#fff7ed", color: "#ea580c", label: "Attendance" };
      case "attendance_event":
        return {
          bg: "#ecfeff",
          color: "#0f766e",
          label: "Attendance Event",
        };
      default:
        return { bg: "#f3f4f6", color: "#4b5563", label: "History" };
    }
  };

  const getHistoryIcon = source => {
    switch (source) {
      case "recruiting":
        return <IconUser size={18} />;
      case "esignature":
        return <IconFileText size={18} />;
      case "team_event":
        return <IconUsers size={18} />;
      case "manager_meeting":
        return <IconCalendar size={18} />;
      case "attendance":
        return <IconChecklist size={18} />;
      case "attendance_event":
        return <IconCalendar size={18} />;
      default:
        return <IconClock size={18} />;
    }
  };

  // Calculate revenue goal progress
  const calculateRevenueProgress = () => {
    const current = rep?.current_revenue || 0;
    const goal = rep?.revenue_goal || 0;
    const remaining = goal > 0 ? goal - current : 0;
    const percentage = goal > 0 ? Math.round((current / goal) * 100) : 0;
    return { current, goal, remaining, percentage };
  };

  const revenue = rep
    ? calculateRevenueProgress()
    : { current: 0, goal: 0, remaining: 0, percentage: 0 };

  // Load default scale templates
  const loadDefaultScaleTemplates = async () => {
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) return;

      // Load commission templates
      const commissionResponse = await fetch(
        getApiEndpoint("/v1/scales/default-scales/COMMISSION"),
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
        }
      );
      if (commissionResponse.ok) {
        const commissionData = await commissionResponse.json();
        setCommissionTemplates(commissionData.data || []);
      }

      // Load override templates
      const overrideResponse = await fetch(
        getApiEndpoint("/v1/scales/default-scales/OVERRIDE"),
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
        }
      );
      if (overrideResponse.ok) {
        const overrideData = await overrideResponse.json();
        setOverrideTemplates(overrideData.data || []);
      }

      // Load marketing templates
      const marketingResponse = await fetch(
        getApiEndpoint("/v1/scales/default-scales/MD"),
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
        }
      );
      if (marketingResponse.ok) {
        const marketingData = await marketingResponse.json();
        setMarketingTemplates(marketingData.data || []);
      }
    } catch (err) {
      // Silently handle error - templates are optional
    }
  };

  // Handle template selection for commission scale
  const handleCommissionTemplateChange = value => {
    setSelectedCommissionTemplate(value);

    if (value) {
      const template = commissionTemplates.find(t => t.id === parseInt(value));
      if (template && template.scale_lines) {
        const newCommissionScale = template.scale_lines.map((line, index) => ({
          id: index + 1,
          revenue_amount: line.revenue_amount.toString(),
          percentage: line.percentage.toString(),
        }));
        setEditFormData(prev => ({
          ...prev,
          commissionScaleLines: newCommissionScale,
        }));
        setDidEditCommissionScale(true);
      }
    }
  };

  // Handle template selection for override scale
  const handleOverrideTemplateChange = value => {
    setSelectedOverrideTemplate(value);

    if (value) {
      const template = overrideTemplates.find(t => t.id === parseInt(value));
      if (template && template.scale_lines) {
        const newOverrideScale = template.scale_lines.map((line, index) => ({
          id: index + 1,
          revenue_amount: line.revenue_amount.toString(),
          percentage: line.percentage.toString(),
        }));
        setEditFormData(prev => ({
          ...prev,
          overrideScaleLines: newOverrideScale,
        }));
        setDidEditManagerPayScale(true);
      }
    }
  };

  // Handle template selection for marketing deal scale
  const handleMarketingTemplateChange = value => {
    setSelectedMarketingTemplate(value);

    if (value) {
      const template = marketingTemplates.find(t => t.id === parseInt(value));
      if (template && template.scale_lines) {
        const newMarketingScale = template.scale_lines.map((line, index) => ({
          id: index + 1,
          revenue_amount: line.revenue_amount.toString(),
          percentage: line.percentage.toString(),
        }));
        setEditFormData(prev => ({
          ...prev,
          mdScaleLines: newMarketingScale,
        }));
        setDidEditManagerPayScale(true);
      }
    }
  };

  // Load history function
  const loadHistory = async () => {
    setIsLoadingHistory(true);
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) {
        setIsLoadingHistory(false);
        return;
      }

      const response = await fetch(getApiEndpoint(`/v1/rep/${id}/history`), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        setHistory(result.data || []);
      } else {
        setHistory([]);
      }
    } catch (err) {
      // Failed to load history
      setHistory([]);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  // Fetch available groups
  const fetchGroups = async () => {
    setLoadingGroups(true);
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) {
        setLoadingGroups(false);
        return;
      }

      const response = await fetch(getApiEndpoint("/v1/groups/owned"), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        setAvailableGroups(result.data || []);
        // Set current rep groups
        setRepGroups(rep?.groups?.map(g => g.id) || []);
      }
    } catch (err) {
      // Failed to fetch groups
    } finally {
      setLoadingGroups(false);
    }
  };

  // Handle opening edit groups modal
  const handleOpenEditGroups = () => {
    fetchGroups();
    setIsEditGroupsModalOpen(true);
  };

  // Toggle group membership
  const toggleGroup = groupId => {
    setRepGroups(prev => {
      if (prev.includes(groupId)) {
        return prev.filter(id => id !== groupId);
      } else {
        return [...prev, groupId];
      }
    });
  };

  // Save group changes
  const handleSaveGroups = async () => {
    setSavingGroups(true);
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) {
        return;
      }

      const response = await fetch(getApiEndpoint(`/v1/rep/${id}/groups`), {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({
          group_ids: repGroups,
        }),
      });

      if (response.ok) {
        await fetchRepDetails();
        setIsEditGroupsModalOpen(false);
      } else {
        const data = await response.json();
        alert(data.message || "Failed to update groups");
      }
    } catch (err) {
      alert("Failed to update groups");
    } finally {
      setSavingGroups(false);
    }
  };

  // Handle delete rep
  const handleDeleteRep = async () => {
    setIsDeleting(true);
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) {
        alert("Authentication failed");
        return;
      }

      const response = await fetch(getApiEndpoint(`/v1/rep/details/${id}`), {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
      });

      if (response.ok) {
        setIsDeleteModalOpen(false);
        if (user?.role === "COMPANY_ADMIN") {
          navigate("/dashboard");
        } else {
          navigate("/reps");
        }
      } else {
        const data = await response.json();
        alert(data.message || "Failed to delete rep");
      }
    } catch (err) {
      alert("Failed to delete rep");
    } finally {
      setIsDeleting(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchRepDetails();
      loadDefaultScaleTemplates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Fetch documents for this rep
  const fetchRepDocuments = async () => {
    setDocumentsLoading(true);
    setDocumentsError(null);
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) return;
      const response = await fetch(getApiEndpoint(`/v1/rep/${id}/documents`), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
      });
      if (response.ok) {
        const result = await response.json();
        setRepDocuments(result.data);
      } else {
        setDocumentsError("Failed to load documents");
      }
    } catch {
      setDocumentsError("Failed to load documents");
    } finally {
      setDocumentsLoading(false);
    }
  };

  // Upload a document for this rep
  const handleUploadDocument = async () => {
    if (!uploadFile) return;
    setUploading(true);
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) return;
      const formData = new FormData();
      formData.append("file", uploadFile);
      formData.append("label", uploadLabel || uploadFile.name);
      const response = await fetch(
        getApiEndpoint(`/v1/rep/${id}/documents/upload`),
        {
          method: "POST",
          headers: { Authorization: `Bearer ${jwt}` },
          body: formData,
        }
      );
      if (response.ok) {
        setIsUploadModalOpen(false);
        setUploadFile(null);
        setUploadLabel("");
        await fetchRepDocuments();
      } else {
        const data = await response.json();
        alert(data.message || "Failed to upload document");
      }
    } catch {
      alert("Failed to upload document");
    } finally {
      setUploading(false);
    }
  };

  // Delete a manually uploaded attachment
  const handleDeleteAttachment = async attachmentId => {
    if (!window.confirm("Delete this file?")) return;
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) return;
      const response = await fetch(
        getApiEndpoint(`/v1/rep/${id}/documents/attachments/${attachmentId}`),
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${jwt}` },
        }
      );
      if (response.ok) {
        await fetchRepDocuments();
      }
    } catch {
      // silent
    }
  };

  useEffect(() => {
    if (activeTab === "history" && history.length === 0 && !isLoadingHistory) {
      loadHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === "documents" && !repDocuments && !documentsLoading) {
      fetchRepDocuments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const DOWNLINE_TABS = [
    {
      id: "overview",
      label: "Overview",
      icon: IconNetwork,
      route: "/downline?tab=overview",
    },
    { id: "rep-details", label: "Rep Details", icon: IconUser, isActive: true },
    {
      id: "rep-health",
      label: "Rep Health",
      icon: IconHeart,
      route: "/downline?tab=rep-health",
    },
    {
      id: "training-stats",
      label: "Training Stats",
      icon: IconActivity,
      route: "/downline?tab=training-stats",
    },
    {
      id: "programs",
      label: "Program Tracking",
      icon: IconTargetArrow,
      route: "/downline?tab=programs",
    },
    {
      id: "events",
      label: "Events",
      icon: IconCalendar,
      route: "/downline?tab=events",
    },
    {
      id: "pitch-reviews",
      label: "Pitch Reviews",
      icon: IconVideo,
      route: "/downline?tab=pitch-reviews",
    },
  ];

  const downlineCustomHeader = (
    <div className="org-page-custom-header">
      <div className="org-page-header-left">
        <h1 className="org-page-main-title">Downline Management</h1>
        <nav className="org-page-nav-tabs" role="tablist">
          {DOWNLINE_TABS.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                role="tab"
                aria-selected={!!tab.isActive}
                className={`org-page-nav-tab${tab.isActive ? " active" : ""}`}
                onClick={() => {
                  if (tab.isActive) return;
                  navigate(tab.route || "/downline");
                }}
              >
                <Icon size={16} />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>
    </div>
  );

  if (loading) {
    return (
      <PageContent customHeader={downlineCustomHeader}>
        <div className="loader-container">
          <Loader size="lg" />
          <p>Loading rep details...</p>
        </div>
      </PageContent>
    );
  }

  if (error && !isEditing) {
    return (
      <PageContent customHeader={downlineCustomHeader}>
        <div className="error-container">
          <p
            style={{ color: "#dc2626", fontSize: "18px", marginBottom: "20px" }}
          >
            Error: {error}
          </p>
          <button
            onClick={() => navigate("/reps")}
            style={{
              padding: "8px 16px",
              borderRadius: "6px",
              border: "1px solid #e5e7eb",
              background: "white",
              cursor: "pointer",
            }}
          >
            Back to Rep Management
          </button>
        </div>
      </PageContent>
    );
  }

  if (!rep) {
    return (
      <PageContent customHeader={downlineCustomHeader}>
        <div className="error-container">
          <p
            style={{ color: "#dc2626", fontSize: "18px", marginBottom: "20px" }}
          >
            Rep not found
          </p>
          <button
            onClick={() => navigate("/reps")}
            style={{
              padding: "8px 16px",
              borderRadius: "6px",
              border: "1px solid #e5e7eb",
              background: "white",
              cursor: "pointer",
            }}
          >
            Back to Rep Management
          </button>
        </div>
      </PageContent>
    );
  }

  return (
    <PageContent customHeader={downlineCustomHeader}>
      <div className="rep-details-page-container">
        {/* Profile Header */}
        <div className="rep-details-profile-header">
          <div className="rep-details-header-actions">
            {!isEditing ? (
              <>
                <button
                  className="rep-details-header-btn"
                  onClick={handleEditClick}
                >
                  <IconEdit size={16} />
                  Edit
                </button>
                <button
                  className="rep-details-header-btn"
                  onClick={() => setIsDeactivateModalOpen(true)}
                >
                  Deactivate Rep
                </button>
                <button
                  className="rep-details-header-btn"
                  onClick={handleOpenEditGroups}
                >
                  Edit Groups
                </button>
                <button
                  className="rep-details-header-btn"
                  onClick={() => setIsDeleteModalOpen(true)}
                  style={{
                    background: "#dc2626",
                    color: "white",
                  }}
                >
                  Delete Rep
                </button>
              </>
            ) : (
              <div
                style={{ display: "flex", flexDirection: "column", gap: "8px" }}
              >
                <button
                  className="rep-details-header-btn"
                  onClick={() => setShowSaveConfirmModal(true)}
                  disabled={saving}
                  style={{
                    background: "#ff6b35",
                    color: "white",
                  }}
                >
                  {saving ? <Loader size="xs" color="white" /> : "Save"}
                </button>
                <button
                  className="rep-details-header-btn"
                  onClick={() => setShowCancelConfirmModal(true)}
                  disabled={saving}
                  style={{
                    background: "transparent",
                    border: "1px solid rgba(255, 255, 255, 0.3)",
                    color: "white",
                  }}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>

          <div className="rep-details-header-content">
            <div className="rep-details-avatar-wrapper">
              {profileImageUrl ? (
                <img
                  src={profileImageUrl}
                  alt="Profile"
                  className="rep-details-avatar"
                />
              ) : (
                <div className="rep-details-avatar-placeholder">
                  {getInitials()}
                </div>
              )}
            </div>

            <div className="rep-details-profile-info">
              {isEditing ? (
                <div
                  style={{ display: "flex", gap: "8px", marginBottom: "8px" }}
                >
                  <input
                    type="text"
                    value={editFormData.firstName}
                    onChange={e =>
                      handleInputChange("firstName", e.target.value)
                    }
                    disabled={saving}
                    placeholder="First Name"
                    style={{
                      padding: "8px 12px",
                      border: "1px solid #e5e7eb",
                      borderRadius: "6px",
                      fontSize: "16px",
                      fontWeight: "600",
                    }}
                  />
                  <input
                    type="text"
                    value={editFormData.lastName}
                    onChange={e =>
                      handleInputChange("lastName", e.target.value)
                    }
                    disabled={saving}
                    placeholder="Last Name"
                    style={{
                      padding: "8px 12px",
                      border: "1px solid #e5e7eb",
                      borderRadius: "6px",
                      fontSize: "16px",
                      fontWeight: "600",
                    }}
                  />
                </div>
              ) : (
                <h1 style={{ color: "white" }}>
                  {rep.first_name} {rep.last_name}
                </h1>
              )}
              <p className="role" style={{ color: "white" }}>
                {rep.rep_type_name || "Sales Representative"}
              </p>
              {/* Group badges */}
              {rep.groups && rep.groups.length > 0 && (
                <div
                  style={{
                    display: "flex",
                    gap: "8px",
                    alignItems: "center",
                    flexWrap: "wrap",
                  }}
                >
                  {rep.groups.map(group => (
                    <span
                      key={group.id}
                      style={{
                        background: "rgba(255, 255, 255, 0.2)",
                        border: "1px solid rgba(255, 255, 255, 0.3)",
                        backdropFilter: "blur(10px)",
                        color: "#ffffff",
                        padding: "6px 12px",
                        borderRadius: "20px",
                        fontSize: "13px",
                        fontWeight: "600",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                      title={`Created by ${group.manager_name || "Unknown"}`}
                    >
                      {group.rep_type && group.rep_type.image && (
                        <img
                          src={group.rep_type.image}
                          alt={group.rep_type.name}
                          style={{
                            width: "16px",
                            height: "16px",
                            borderRadius: "50%",
                            objectFit: "cover",
                          }}
                        />
                      )}
                      {group.name}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div
          style={{
            display: "flex",
            gap: "8px",
            padding: "0 24px",
            marginBottom: "12px",
            borderBottom: "2px solid #e5e7eb",
          }}
        >
          <button
            onClick={() => setActiveTab("details")}
            style={{
              padding: "12px 24px",
              background: "transparent",
              border: "none",
              borderBottom:
                activeTab === "details"
                  ? "2px solid #ff6b35"
                  : "2px solid transparent",
              color: activeTab === "details" ? "#ff6b35" : "#6b7280",
              fontSize: "15px",
              fontWeight: activeTab === "details" ? "600" : "500",
              cursor: "pointer",
              marginBottom: "-2px",
              transition: "all 0.2s",
            }}
          >
            Rep Details
          </button>
          <button
            onClick={() => setActiveTab("history")}
            style={{
              padding: "12px 24px",
              background: "transparent",
              border: "none",
              borderBottom:
                activeTab === "history"
                  ? "2px solid #ff6b35"
                  : "2px solid transparent",
              color: activeTab === "history" ? "#ff6b35" : "#6b7280",
              fontSize: "15px",
              fontWeight: activeTab === "history" ? "600" : "500",
              cursor: "pointer",
              marginBottom: "-2px",
              transition: "all 0.2s",
            }}
          >
            History
          </button>
          <button
            onClick={() => setActiveTab("documents")}
            style={{
              padding: "12px 24px",
              background: "transparent",
              border: "none",
              borderBottom:
                activeTab === "documents"
                  ? "2px solid #ff6b35"
                  : "2px solid transparent",
              color: activeTab === "documents" ? "#ff6b35" : "#6b7280",
              fontSize: "15px",
              fontWeight: activeTab === "documents" ? "600" : "500",
              cursor: "pointer",
              marginBottom: "-2px",
              transition: "all 0.2s",
            }}
          >
            Documents
          </button>
          <button
            onClick={() => setActiveTab("deal")}
            style={{
              padding: "12px 24px",
              background: "transparent",
              border: "none",
              borderBottom:
                activeTab === "deal"
                  ? "2px solid #ff6b35"
                  : "2px solid transparent",
              color: activeTab === "deal" ? "#ff6b35" : "#6b7280",
              fontSize: "15px",
              fontWeight: activeTab === "deal" ? "600" : "500",
              cursor: "pointer",
              marginBottom: "-2px",
              transition: "all 0.2s",
            }}
          >
            Deal
          </button>
        </div>

        {/* Unified Profile Information Section */}
        {activeTab === "details" && (
          <div className="rep-details-info-section">
            {/* Error Display */}
            {error && isEditing && (
              <Alert
                icon={<IconAlertCircle size={16} />}
                title="Error Saving Changes"
                color="red"
                variant="light"
                style={{ marginBottom: "24px" }}
                withCloseButton
                onClose={() => setError(null)}
              >
                {error}
              </Alert>
            )}

            {/* Revenue Pacing Section */}
            {rep && (
              <div
                className="revenue-goal-section"
                style={{ padding: "16px 20px", marginBottom: "40px" }}
              >
                <div className="revenue-goal-header">
                  <h3 style={{ fontSize: "16px" }}>
                    <IconTrophy className="trophy-icon" size={18} />
                    Personal Rep Goal
                  </h3>
                  <span className="completion-text">
                    {revenue.percentage}% Complete
                  </span>
                </div>
                <div className="revenue-goal-content">
                  <div>
                    <p className="revenue-current">
                      ${revenue.current.toLocaleString()}
                    </p>
                    {isEditing ? (
                      <div style={{ marginTop: "8px" }}>
                        <input
                          type="text"
                          value={editFormData.revenueGoal}
                          onChange={e =>
                            handleRevenueGoalChange(e.target.value)
                          }
                          disabled={saving}
                          placeholder="Enter goal amount"
                          style={{
                            padding: "8px 12px",
                            border: "1px solid #e5e7eb",
                            borderRadius: "6px",
                            fontSize: "14px",
                            width: "200px",
                          }}
                        />
                      </div>
                    ) : (
                      <p className="revenue-goal-text">
                        of ${revenue.goal.toLocaleString()} goal
                      </p>
                    )}
                  </div>
                  <div className="revenue-remaining">
                    <p className="amount">
                      ${revenue.remaining.toLocaleString()}
                    </p>
                    <p className="label">remaining</p>
                  </div>
                </div>
                <div className="progress-bar-container">
                  <div
                    className="progress-bar"
                    style={{ width: `${revenue.percentage}%` }}
                  ></div>
                </div>
              </div>
            )}

            {/* Contact Information & Personal Details Grid */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "48px",
                marginBottom: "40px",
              }}
            >
              {/* Contact Information Column */}
              <div>
                <h2 style={{ marginBottom: "24px" }}>
                  <IconUser />
                  Contact Information
                </h2>
                <div className="rep-details-info-grid single-column">
                  <div className="rep-details-info-item">
                    <div className="rep-details-info-icon email">
                      <IconMail />
                    </div>
                    <div className="rep-details-info-content">
                      <p className="rep-details-info-label">EMAIL</p>
                      {isEditing ? (
                        <input
                          type="email"
                          value={editFormData.email}
                          onChange={e =>
                            handleInputChange("email", e.target.value)
                          }
                          disabled={saving}
                          style={{
                            width: "100%",
                            padding: "8px 12px",
                            border: "1px solid #e5e7eb",
                            borderRadius: "6px",
                            fontSize: "14px",
                            marginTop: "4px",
                          }}
                        />
                      ) : (
                        <p className="rep-details-info-value">
                          {rep.email ? (
                            <a href={`mailto:${rep.email}`}>{rep.email}</a>
                          ) : (
                            "N/A"
                          )}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="rep-details-info-item">
                    <div className="rep-details-info-icon phone">
                      <IconPhone />
                    </div>
                    <div className="rep-details-info-content">
                      <p className="rep-details-info-label">PHONE</p>
                      {isEditing ? (
                        <input
                          type="tel"
                          value={editFormData.phoneNumber}
                          onChange={e => handlePhoneChange(e.target.value)}
                          disabled={saving}
                          placeholder="(555) 123-4567"
                          maxLength={14}
                          style={{
                            width: "100%",
                            padding: "8px 12px",
                            border: "1px solid #e5e7eb",
                            borderRadius: "6px",
                            fontSize: "14px",
                            marginTop: "4px",
                          }}
                        />
                      ) : (
                        <p className="rep-details-info-value">
                          {formatPhoneNumber(rep.phone_number)}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="rep-details-info-item">
                    <div className="rep-details-info-icon instagram">
                      <IconBrandInstagram />
                    </div>
                    <div className="rep-details-info-content">
                      <p className="rep-details-info-label">INSTAGRAM</p>
                      {isEditing ? (
                        <input
                          type="text"
                          value={editFormData.instagram}
                          onChange={e =>
                            handleInputChange("instagram", e.target.value)
                          }
                          disabled={saving}
                          placeholder="@username or URL"
                          style={{
                            width: "100%",
                            padding: "8px 12px",
                            border: "1px solid #e5e7eb",
                            borderRadius: "6px",
                            fontSize: "14px",
                            marginTop: "4px",
                          }}
                        />
                      ) : (
                        <p className="rep-details-info-value">
                          {rep.instagram ? (
                            rep.instagram.startsWith("http") ||
                            rep.instagram.startsWith("@") ||
                            rep.instagram.includes(".com") ? (
                              <a
                                href={
                                  rep.instagram.startsWith("http")
                                    ? rep.instagram
                                    : rep.instagram.startsWith("@")
                                      ? `https://instagram.com/${rep.instagram.replace("@", "")}`
                                      : `https://${rep.instagram}`
                                }
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {rep.instagram}
                              </a>
                            ) : (
                              rep.instagram
                            )
                          ) : (
                            "N/A"
                          )}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="rep-details-info-item">
                    <div
                      className="rep-details-info-icon"
                      style={{ background: "#e0e7ff", color: "#0077b5" }}
                    >
                      <IconBrandLinkedin />
                    </div>
                    <div className="rep-details-info-content">
                      <p className="rep-details-info-label">LINKEDIN</p>
                      {isEditing ? (
                        <input
                          type="text"
                          value={editFormData.linkedin}
                          onChange={e =>
                            handleInputChange("linkedin", e.target.value)
                          }
                          disabled={saving}
                          placeholder="LinkedIn profile URL"
                          style={{
                            width: "100%",
                            padding: "8px 12px",
                            border: "1px solid #e5e7eb",
                            borderRadius: "6px",
                            fontSize: "14px",
                            marginTop: "4px",
                          }}
                        />
                      ) : (
                        <p className="rep-details-info-value">
                          {rep.linked_in ? (
                            rep.linked_in.startsWith("http") ||
                            rep.linked_in.includes(".com") ? (
                              <a
                                href={
                                  rep.linked_in.startsWith("http")
                                    ? rep.linked_in
                                    : `https://${rep.linked_in}`
                                }
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {rep.linked_in}
                              </a>
                            ) : (
                              rep.linked_in
                            )
                          ) : (
                            "N/A"
                          )}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Personal Details Column */}
              <div>
                <h2 style={{ marginBottom: "24px" }}>
                  <IconUser />
                  Personal Details
                </h2>
                <div className="rep-details-info-grid single-column">
                  <div className="rep-details-info-item">
                    <div className="rep-details-info-icon birthday">
                      <IconCalendar />
                    </div>
                    <div className="rep-details-info-content">
                      <p className="rep-details-info-label">BIRTHDAY</p>
                      {isEditing ? (
                        <input
                          type="date"
                          value={editFormData.birthday}
                          onChange={e =>
                            handleInputChange("birthday", e.target.value)
                          }
                          disabled={saving}
                          style={{
                            width: "100%",
                            padding: "8px 12px",
                            border: "1px solid #e5e7eb",
                            borderRadius: "6px",
                            fontSize: "14px",
                            marginTop: "4px",
                          }}
                        />
                      ) : (
                        <p className="rep-details-info-value">
                          {formatDate(rep.birthday)}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="rep-details-info-item">
                    <div
                      className="rep-details-info-icon"
                      style={{
                        background: editFormData.isManager
                          ? "#dcfce7"
                          : "#fee2e2",
                        color: editFormData.isManager ? "#16a34a" : "#dc2626",
                      }}
                    >
                      <IconUser />
                    </div>
                    <div className="rep-details-info-content">
                      <p className="rep-details-info-label">
                        HAS MANAGER PRIVILEGES?
                      </p>
                      {isEditing ? (
                        <div
                          style={{
                            display: "flex",
                            gap: "4px",
                            background: "#f3f4f6",
                            padding: "2px",
                            borderRadius: "6px",
                            marginTop: "4px",
                            width: "fit-content",
                          }}
                        >
                          <button
                            onClick={() =>
                              handleInputChange("isManager", false)
                            }
                            disabled={saving}
                            style={{
                              padding: "4px 16px",
                              background: !editFormData.isManager
                                ? "white"
                                : "transparent",
                              border: "none",
                              borderRadius: "4px",
                              fontSize: "13px",
                              fontWeight: "500",
                              cursor: "pointer",
                              color: !editFormData.isManager
                                ? "#dc2626"
                                : "#6b7280",
                              boxShadow: !editFormData.isManager
                                ? "0 1px 3px rgba(0,0,0,0.1)"
                                : "none",
                            }}
                          >
                            No
                          </button>
                          <button
                            onClick={() => handleInputChange("isManager", true)}
                            disabled={saving}
                            style={{
                              padding: "4px 16px",
                              background: editFormData.isManager
                                ? "white"
                                : "transparent",
                              border: "none",
                              borderRadius: "4px",
                              fontSize: "13px",
                              fontWeight: "500",
                              cursor: "pointer",
                              color: editFormData.isManager
                                ? "#16a34a"
                                : "#6b7280",
                              boxShadow: editFormData.isManager
                                ? "0 1px 3px rgba(0,0,0,0.1)"
                                : "none",
                            }}
                          >
                            Yes
                          </button>
                        </div>
                      ) : (
                        <p
                          className="rep-details-info-value"
                          style={{
                            color: rep.is_manager ? "#16a34a" : "#dc2626",
                            fontWeight: "600",
                          }}
                        >
                          {rep.is_manager ? "Yes" : "No"}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="rep-details-info-item">
                    <div className="rep-details-info-icon manager">
                      <IconUser />
                    </div>
                    <div className="rep-details-info-content">
                      <p className="rep-details-info-label">MANAGER</p>
                      {isEditing ? (
                        <select
                          value={editFormData.manager}
                          onChange={e =>
                            handleInputChange("manager", e.target.value)
                          }
                          disabled={saving}
                          style={{
                            width: "100%",
                            padding: "8px 12px",
                            border: "1px solid #e5e7eb",
                            borderRadius: "6px",
                            fontSize: "14px",
                            marginTop: "4px",
                            backgroundColor: "white",
                            cursor: "pointer",
                          }}
                        >
                          <option value="">Select manager...</option>
                          {reps
                            .filter(r => r.is_manager)
                            .map(r => (
                              <option key={r.rep_id} value={r.rep_id}>
                                {r.first_name} {r.last_name}
                              </option>
                            ))}
                        </select>
                      ) : (
                        <p className="rep-details-info-value">
                          {rep.manager_name || "N/A"}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="rep-details-info-item">
                    <div
                      className="rep-details-info-icon"
                      style={{ background: "#dbeafe", color: "#2563eb" }}
                    >
                      <IconUser />
                    </div>
                    <div className="rep-details-info-content">
                      <p className="rep-details-info-label">RECRUITED BY</p>
                      {isEditing ? (
                        <select
                          value={editFormData.recruitedBy}
                          onChange={e =>
                            handleInputChange("recruitedBy", e.target.value)
                          }
                          disabled={saving}
                          style={{
                            width: "100%",
                            padding: "8px 12px",
                            border: "1px solid #e5e7eb",
                            borderRadius: "6px",
                            fontSize: "14px",
                            marginTop: "4px",
                            backgroundColor: "white",
                            cursor: "pointer",
                          }}
                        >
                          <option value="">Select recruiter...</option>
                          {reps.map(r => (
                            <option key={r.rep_id} value={r.rep_id}>
                              {r.first_name} {r.last_name}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <p className="rep-details-info-value">
                          {rep.recruited_by_name || "N/A"}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Employment Details Section */}
            <h2 style={{ marginBottom: "24px" }}>
              <IconClock />
              Employment Details
            </h2>
            <div
              className="rep-details-info-grid three-columns"
              style={{ marginBottom: "40px" }}
            >
              <div className="rep-details-info-item">
                <div
                  className="rep-details-info-icon"
                  style={{ background: "#fef3c7", color: "#f59e0b" }}
                >
                  <IconUser />
                </div>
                <div className="rep-details-info-content">
                  <p className="rep-details-info-label">RECRUITING SOURCE</p>
                  {isEditing ? (
                    <select
                      value={editFormData.recruitingSource}
                      onChange={e =>
                        handleInputChange("recruitingSource", e.target.value)
                      }
                      disabled={saving}
                      style={{
                        width: "100%",
                        padding: "8px 12px",
                        border: "1px solid #e5e7eb",
                        borderRadius: "6px",
                        fontSize: "14px",
                        marginTop: "4px",
                        backgroundColor: "white",
                        cursor: "pointer",
                      }}
                    >
                      <option value="">Select recruiting source...</option>
                      {recruitingSourceTypes.map(source => (
                        <option key={source.id} value={source.id}>
                          {source.source_name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <p className="rep-details-info-value">
                      {rep.recruiting_source_name || "N/A"}
                    </p>
                  )}
                </div>
              </div>

              {rep.recruiting_source_notes && (
                <div className="rep-details-info-item">
                  <div
                    className="rep-details-info-icon"
                    style={{ background: "#fef3c7", color: "#f59e0b" }}
                  >
                    <IconUser />
                  </div>
                  <div className="rep-details-info-content">
                    <p className="rep-details-info-label">
                      RECRUITING SOURCE NOTES
                    </p>
                    <p className="rep-details-info-value">
                      {rep.recruiting_source_notes}
                    </p>
                  </div>
                </div>
              )}

              <div className="rep-details-info-item">
                <div className="rep-details-info-icon start-date">
                  <IconCalendar />
                </div>
                <div className="rep-details-info-content">
                  <p className="rep-details-info-label">START DATE</p>
                  {isEditing ? (
                    <input
                      type="date"
                      value={editFormData.saleStartDate}
                      onChange={e =>
                        handleInputChange("saleStartDate", e.target.value)
                      }
                      disabled={saving}
                      style={{
                        width: "100%",
                        padding: "8px 12px",
                        border: "1px solid #e5e7eb",
                        borderRadius: "6px",
                        fontSize: "14px",
                        marginTop: "4px",
                      }}
                    />
                  ) : (
                    <p
                      className="rep-details-info-value"
                      style={{ fontSize: "14px" }}
                    >
                      {formatMonthDay(rep.sale_start_date)}
                    </p>
                  )}
                </div>
              </div>

              <div className="rep-details-info-item">
                <div className="rep-details-info-icon end-date">
                  <IconCalendar />
                </div>
                <div className="rep-details-info-content">
                  <p className="rep-details-info-label">END DATE</p>
                  {isEditing ? (
                    <input
                      type="date"
                      value={editFormData.saleEndDate}
                      onChange={e =>
                        handleInputChange("saleEndDate", e.target.value)
                      }
                      disabled={saving}
                      style={{
                        width: "100%",
                        padding: "8px 12px",
                        border: "1px solid #e5e7eb",
                        borderRadius: "6px",
                        fontSize: "14px",
                        marginTop: "4px",
                      }}
                    />
                  ) : (
                    <p className="rep-details-info-value">
                      {formatDate(rep.sale_end_date)}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Compensation Details */}
            <h2 style={{ marginBottom: "24px" }}>
              <IconCurrencyDollar />
              Compensation Details
            </h2>
            {!showCompensation ? (
              <div className="rep-details-compensation-empty">
                <p style={{ marginBottom: "1.5rem", maxWidth: "500px" }}>
                  Compensation information is protected for security. Click
                  below to view projected revenue and commission structures.
                </p>
                <button
                  className="btn btn-lg btn-primary"
                  onClick={handleViewCompensation}
                  style={{ padding: "12px 32px", fontSize: "16px" }}
                >
                  <IconLock size={20} style={{ marginRight: "8px" }} /> View
                  Compensation Details
                </button>
              </div>
            ) : (
              <>
                {/* User Seasons Section */}
                {compensation?.user_seasons &&
                  compensation.user_seasons.length > 0 && (
                    <div style={{ marginBottom: "40px" }}>
                      <h3
                        style={{
                          fontSize: "16px",
                          fontWeight: "600",
                          marginBottom: "16px",
                        }}
                      >
                        Season Information
                      </h3>
                      <div style={{ display: "grid", gap: "16px" }}>
                        {compensation.user_seasons.map(userSeason => (
                          <div
                            key={userSeason.id}
                            style={{
                              border: "1px solid #e5e7eb",
                              borderRadius: "8px",
                              padding: "16px",
                              background: "white",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "flex-start",
                                marginBottom: "12px",
                              }}
                            >
                              <div>
                                <h4
                                  style={{
                                    margin: 0,
                                    fontSize: "15px",
                                    fontWeight: "600",
                                    marginBottom: "4px",
                                  }}
                                >
                                  {userSeason.season?.name || "Season"}
                                </h4>
                                <p
                                  style={{
                                    margin: 0,
                                    fontSize: "13px",
                                    color: "#6b7280",
                                  }}
                                >
                                  {userSeason.season?.start_date &&
                                    new Date(
                                      userSeason.season.start_date
                                    ).toLocaleDateString("en-US", {
                                      month: "short",
                                      day: "numeric",
                                      year: "numeric",
                                    })}
                                  {" - "}
                                  {userSeason.season?.end_date &&
                                    new Date(
                                      userSeason.season.end_date
                                    ).toLocaleDateString("en-US", {
                                      month: "short",
                                      day: "numeric",
                                      year: "numeric",
                                    })}
                                </p>
                              </div>
                              {userSeason.season?.is_active && (
                                <span
                                  style={{
                                    background: "#dcfce7",
                                    color: "#16a34a",
                                    padding: "4px 12px",
                                    borderRadius: "12px",
                                    fontSize: "12px",
                                    fontWeight: "600",
                                  }}
                                >
                                  Active
                                </span>
                              )}
                            </div>
                            <div
                              style={{
                                display: "grid",
                                gridTemplateColumns:
                                  "repeat(auto-fit, minmax(200px, 1fr))",
                                gap: "16px",
                              }}
                            >
                              <div>
                                <p
                                  style={{
                                    fontSize: "12px",
                                    color: "#6b7280",
                                    marginBottom: "4px",
                                    fontWeight: "600",
                                  }}
                                >
                                  PROJECTED REVENUE
                                </p>
                                <p
                                  style={{
                                    fontSize: "18px",
                                    fontWeight: "600",
                                    color: "#111827",
                                    margin: 0,
                                  }}
                                >
                                  $
                                  {Number(
                                    userSeason.projected_revenue || 0
                                  ).toLocaleString()}
                                </p>
                              </div>
                              <div>
                                <p
                                  style={{
                                    fontSize: "12px",
                                    color: "#6b7280",
                                    marginBottom: "4px",
                                    fontWeight: "600",
                                  }}
                                >
                                  TEAM
                                </p>
                                <p
                                  style={{
                                    fontSize: "14px",
                                    color: "#111827",
                                    margin: 0,
                                  }}
                                >
                                  {userSeason.team?.name || "N/A"}
                                </p>
                              </div>
                              <div>
                                <p
                                  style={{
                                    fontSize: "12px",
                                    color: "#6b7280",
                                    marginBottom: "4px",
                                    fontWeight: "600",
                                  }}
                                >
                                  ROLE
                                </p>
                                <p
                                  style={{
                                    fontSize: "14px",
                                    color: "#111827",
                                    margin: 0,
                                  }}
                                >
                                  {userSeason.role?.name || "N/A"}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "24px",
                    marginTop: "24px",
                  }}
                >
                  <h3
                    style={{ margin: 0, fontSize: "16px", fontWeight: "600" }}
                  >
                    Pay Scales
                  </h3>
                  <button
                    onClick={() => {
                      setShowCompensation(false);
                      setCompensation(null);
                    }}
                    style={{
                      background: "transparent",
                      border: "1px solid #e5e7eb",
                      color: "#6b7280",
                      padding: "8px 16px",
                      borderRadius: "6px",
                      fontSize: "14px",
                      cursor: "pointer",
                      transition: "all 0.2s",
                    }}
                    onMouseEnter={e => {
                      e.target.style.background = "#f9fafb";
                      e.target.style.borderColor = "#d1d5db";
                    }}
                    onMouseLeave={e => {
                      e.target.style.background = "transparent";
                      e.target.style.borderColor = "#e5e7eb";
                    }}
                  >
                    Hide Compensation Details
                  </button>
                </div>

                {/* Scales displayed from user_scales */}
                {!isEditing ? (
                  <>
                    {compensation?.user_scales &&
                    compensation.user_scales.length > 0 ? (
                      compensation.user_scales.map(userScale => (
                        <div
                          key={userScale.id}
                          style={{ marginBottom: "32px" }}
                        >
                          <h4
                            style={{
                              fontSize: "15px",
                              fontWeight: "600",
                              marginBottom: "12px",
                            }}
                          >
                            {userScale.name}
                          </h4>
                          {userScale.seasons &&
                            userScale.seasons.length > 0 && (
                              <p
                                style={{
                                  fontSize: "13px",
                                  color: "#6b7280",
                                  marginBottom: "12px",
                                }}
                              >
                                Seasons:{" "}
                                {userScale.seasons.map(s => s.name).join(", ")}
                              </p>
                            )}
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns:
                                "repeat(auto-fit, minmax(300px, 1fr))",
                              gap: "24px",
                            }}
                          >
                            <ScalesPreview
                              scale_lines={
                                userScale.scale_lines &&
                                userScale.scale_lines.length > 0
                                  ? userScale.scale_lines
                                  : [
                                      {
                                        revenue_amount: 0,
                                        percentage: 0,
                                      },
                                    ]
                              }
                              name={
                                userScale.type === "COMMISSION"
                                  ? "Commission"
                                  : userScale.type === "OVERRIDE"
                                    ? "Override"
                                    : "Marketing Deal"
                              }
                              showActions={false}
                              showFooter={false}
                              isEmpty={
                                !userScale.scale_lines ||
                                userScale.scale_lines.length === 0
                              }
                            />
                          </div>
                        </div>
                      ))
                    ) : (
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns:
                            "repeat(auto-fit, minmax(300px, 1fr))",
                          gap: "24px",
                          marginBottom: "20px",
                        }}
                      >
                        <div
                          onClick={() => {
                            if (
                              !compensation?.commission_scale_lines ||
                              compensation.commission_scale_lines.length === 0
                            ) {
                              setEditScaleType("commission_scale");
                            }
                          }}
                          style={{
                            cursor:
                              !compensation?.commission_scale_lines ||
                              compensation.commission_scale_lines.length === 0
                                ? "pointer"
                                : "default",
                          }}
                        >
                          <ScalesPreview
                            scale_lines={
                              compensation?.commission_scale_lines &&
                              compensation.commission_scale_lines.length > 0
                                ? compensation.commission_scale_lines
                                : [
                                    {
                                      revenue_threshold: 0,
                                      commission_percentage: 0,
                                    },
                                  ]
                            }
                            name="Commission Scale"
                            showActions={false}
                            showFooter={false}
                            isEmpty={
                              !compensation?.commission_scale_lines ||
                              compensation.commission_scale_lines.length === 0
                            }
                          />
                        </div>

                        {(() => {
                          const hasOverride =
                            compensation?.override_scale_lines &&
                            compensation.override_scale_lines.length > 0;
                          const hasMD =
                            compensation?.md_scale_lines &&
                            compensation.md_scale_lines.length > 0;

                          let scaleLines, scaleName, isEmpty;

                          if (hasOverride) {
                            scaleLines = compensation.override_scale_lines;
                            scaleName = "Override Scale";
                            isEmpty = false;
                          } else if (hasMD) {
                            scaleLines = compensation.md_scale_lines;
                            scaleName = "Marketing Deal Scale";
                            isEmpty = false;
                          } else {
                            scaleLines = [
                              {
                                revenue_threshold: 0,
                                commission_percentage: 0,
                              },
                            ];
                            scaleName = "Manager Pay";
                            isEmpty = true;
                          }

                          return (
                            <div
                              onClick={() => {
                                if (isEmpty) {
                                  setIsManagerPayTypeSelectionOpen(true);
                                }
                              }}
                              style={{
                                cursor: isEmpty ? "pointer" : "default",
                              }}
                            >
                              <ScalesPreview
                                scale_lines={scaleLines}
                                name={scaleName}
                                showActions={false}
                                showFooter={false}
                                isEmpty={isEmpty}
                              />
                            </div>
                          );
                        })()}
                      </div>
                    )}
                  </>
                ) : (
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(300px, 1fr))",
                      gap: "24px",
                      marginBottom: "20px",
                    }}
                  >
                    {/* Commission Scale Editor */}
                    <div
                      style={{
                        border: "1px solid #e5e7eb",
                        borderRadius: "8px",
                        padding: "16px",
                        background: "white",
                      }}
                    >
                      <h4 style={{ marginTop: 0, marginBottom: "12px" }}>
                        Commission Scale
                      </h4>
                      {commissionTemplates.length > 0 && (
                        <div style={{ marginBottom: "12px" }}>
                          <select
                            value={selectedCommissionTemplate}
                            onChange={e =>
                              handleCommissionTemplateChange(e.target.value)
                            }
                            disabled={saving}
                            style={{
                              width: "100%",
                              padding: "8px 12px",
                              border: "1px solid #e5e7eb",
                              borderRadius: "6px",
                              fontSize: "13px",
                              backgroundColor: "white",
                              cursor: "pointer",
                            }}
                          >
                            <option value="">Use template...</option>
                            {commissionTemplates.map(template => (
                              <option key={template.id} value={template.id}>
                                {template.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                      <table
                        style={{ width: "100%", borderCollapse: "collapse" }}
                      >
                        <thead>
                          <tr>
                            <th
                              style={{
                                textAlign: "left",
                                padding: "8px",
                                borderBottom: "2px solid #e5e7eb",
                                fontSize: "12px",
                                fontWeight: "600",
                                color: "#6b7280",
                              }}
                            >
                              Revenue ($)
                            </th>
                            <th
                              style={{
                                textAlign: "left",
                                padding: "8px",
                                borderBottom: "2px solid #e5e7eb",
                                fontSize: "12px",
                                fontWeight: "600",
                                color: "#6b7280",
                              }}
                            >
                              %
                            </th>
                            <th
                              style={{
                                width: "40px",
                                borderBottom: "2px solid #e5e7eb",
                              }}
                            ></th>
                          </tr>
                        </thead>
                        <tbody>
                          {editFormData.commissionScaleLines.map(line => (
                            <tr key={line.id}>
                              <td style={{ padding: "8px 8px 8px 8px" }}>
                                <input
                                  type="number"
                                  value={line.revenue_amount}
                                  onChange={e =>
                                    handleScaleLineChange(
                                      "commissionScaleLines",
                                      line.id,
                                      "revenue_amount",
                                      e.target.value
                                    )
                                  }
                                  disabled={saving}
                                  placeholder="0"
                                  min="0"
                                  step="0.01"
                                  style={{
                                    width: "100%",
                                    padding: "6px 8px",
                                    border: "1px solid #e5e7eb",
                                    borderRadius: "4px",
                                    fontSize: "14px",
                                  }}
                                />
                              </td>
                              <td style={{ padding: "8px" }}>
                                <input
                                  type="number"
                                  value={line.percentage}
                                  onChange={e =>
                                    handleScaleLineChange(
                                      "commissionScaleLines",
                                      line.id,
                                      "percentage",
                                      e.target.value
                                    )
                                  }
                                  disabled={saving}
                                  placeholder="0"
                                  min="0"
                                  max="100"
                                  step="0.01"
                                  style={{
                                    width: "100%",
                                    padding: "6px 8px",
                                    border: "1px solid #e5e7eb",
                                    borderRadius: "4px",
                                    fontSize: "14px",
                                  }}
                                />
                              </td>
                              <td
                                style={{ padding: "8px", textAlign: "center" }}
                              >
                                <button
                                  onClick={() =>
                                    removeScaleLine(
                                      "commissionScaleLines",
                                      line.id
                                    )
                                  }
                                  disabled={
                                    editFormData.commissionScaleLines.length <=
                                      1 || saving
                                  }
                                  style={{
                                    background: "transparent",
                                    border: "none",
                                    color: "#dc2626",
                                    cursor:
                                      editFormData.commissionScaleLines
                                        .length <= 1
                                        ? "not-allowed"
                                        : "pointer",
                                    padding: "4px",
                                    opacity:
                                      editFormData.commissionScaleLines
                                        .length <= 1
                                        ? 0.4
                                        : 1,
                                  }}
                                >
                                  ×
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <button
                        onClick={() => addScaleLine("commissionScaleLines")}
                        disabled={saving}
                        style={{
                          marginTop: "12px",
                          padding: "6px 12px",
                          background: "transparent",
                          border: "1px solid #e5e7eb",
                          borderRadius: "4px",
                          fontSize: "13px",
                          cursor: "pointer",
                          color: "#6b7280",
                        }}
                      >
                        + Add Row
                      </button>
                    </div>

                    {/* Manager Pay Scale Editor (Override or MD) */}
                    {(() => {
                      const scaleType =
                        managerPayType === "override"
                          ? "overrideScaleLines"
                          : "mdScaleLines";
                      const scaleName =
                        managerPayType === "override"
                          ? "Override Scale"
                          : "Marketing Deal Scale";
                      const scaleLines = editFormData[scaleType];

                      return (
                        <div
                          style={{
                            border: "1px solid #e5e7eb",
                            borderRadius: "8px",
                            padding: "16px",
                            background: "white",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              marginBottom: "12px",
                            }}
                          >
                            <h4 style={{ margin: 0 }}>{scaleName}</h4>
                            <div
                              style={{
                                display: "flex",
                                gap: "4px",
                                background: "#f3f4f6",
                                padding: "2px",
                                borderRadius: "6px",
                              }}
                            >
                              <button
                                onClick={() => {
                                  setDidEditManagerPayScale(true);
                                  setManagerPayType("override");
                                }}
                                disabled={saving}
                                style={{
                                  padding: "4px 12px",
                                  background:
                                    managerPayType === "override"
                                      ? "white"
                                      : "transparent",
                                  border: "none",
                                  borderRadius: "4px",
                                  fontSize: "12px",
                                  fontWeight: "500",
                                  cursor: "pointer",
                                  color:
                                    managerPayType === "override"
                                      ? "#1f2937"
                                      : "#6b7280",
                                  boxShadow:
                                    managerPayType === "override"
                                      ? "0 1px 3px rgba(0,0,0,0.1)"
                                      : "none",
                                }}
                              >
                                Override
                              </button>
                              <button
                                onClick={() => {
                                  setDidEditManagerPayScale(true);
                                  setManagerPayType("md");
                                }}
                                disabled={saving}
                                style={{
                                  padding: "4px 12px",
                                  background:
                                    managerPayType === "md"
                                      ? "white"
                                      : "transparent",
                                  border: "none",
                                  borderRadius: "4px",
                                  fontSize: "12px",
                                  fontWeight: "500",
                                  cursor: "pointer",
                                  color:
                                    managerPayType === "md"
                                      ? "#1f2937"
                                      : "#6b7280",
                                  boxShadow:
                                    managerPayType === "md"
                                      ? "0 1px 3px rgba(0,0,0,0.1)"
                                      : "none",
                                }}
                              >
                                Marketing Deal
                              </button>
                            </div>
                          </div>
                          {(managerPayType === "override" &&
                            overrideTemplates.length > 0) ||
                          (managerPayType === "md" &&
                            marketingTemplates.length > 0) ? (
                            <div style={{ marginBottom: "12px" }}>
                              <select
                                value={
                                  managerPayType === "override"
                                    ? selectedOverrideTemplate
                                    : selectedMarketingTemplate
                                }
                                onChange={e =>
                                  managerPayType === "override"
                                    ? handleOverrideTemplateChange(
                                        e.target.value
                                      )
                                    : handleMarketingTemplateChange(
                                        e.target.value
                                      )
                                }
                                disabled={saving}
                                style={{
                                  width: "100%",
                                  padding: "8px 12px",
                                  border: "1px solid #e5e7eb",
                                  borderRadius: "6px",
                                  fontSize: "13px",
                                  backgroundColor: "white",
                                  cursor: "pointer",
                                }}
                              >
                                <option value="">Use template...</option>
                                {(managerPayType === "override"
                                  ? overrideTemplates
                                  : marketingTemplates
                                ).map(template => (
                                  <option key={template.id} value={template.id}>
                                    {template.name}
                                  </option>
                                ))}
                              </select>
                            </div>
                          ) : null}
                          {scaleLines.length > 0 ? (
                            <>
                              <table
                                style={{
                                  width: "100%",
                                  borderCollapse: "collapse",
                                }}
                              >
                                <thead>
                                  <tr>
                                    <th
                                      style={{
                                        textAlign: "left",
                                        padding: "8px",
                                        borderBottom: "2px solid #e5e7eb",
                                        fontSize: "12px",
                                        fontWeight: "600",
                                        color: "#6b7280",
                                      }}
                                    >
                                      Revenue ($)
                                    </th>
                                    <th
                                      style={{
                                        textAlign: "left",
                                        padding: "8px",
                                        borderBottom: "2px solid #e5e7eb",
                                        fontSize: "12px",
                                        fontWeight: "600",
                                        color: "#6b7280",
                                      }}
                                    >
                                      %
                                    </th>
                                    <th
                                      style={{
                                        width: "40px",
                                        borderBottom: "2px solid #e5e7eb",
                                      }}
                                    ></th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {scaleLines.map(line => (
                                    <tr key={line.id}>
                                      <td
                                        style={{ padding: "8px 8px 8px 8px" }}
                                      >
                                        <input
                                          type="number"
                                          value={line.revenue_amount}
                                          onChange={e =>
                                            handleScaleLineChange(
                                              scaleType,
                                              line.id,
                                              "revenue_amount",
                                              e.target.value
                                            )
                                          }
                                          disabled={saving}
                                          placeholder="0"
                                          min="0"
                                          step="0.01"
                                          style={{
                                            width: "100%",
                                            padding: "6px 8px",
                                            border: "1px solid #e5e7eb",
                                            borderRadius: "4px",
                                            fontSize: "14px",
                                          }}
                                        />
                                      </td>
                                      <td style={{ padding: "8px" }}>
                                        <input
                                          type="number"
                                          value={line.percentage}
                                          onChange={e =>
                                            handleScaleLineChange(
                                              scaleType,
                                              line.id,
                                              "percentage",
                                              e.target.value
                                            )
                                          }
                                          disabled={saving}
                                          placeholder="0"
                                          min="0"
                                          max="100"
                                          step="0.01"
                                          style={{
                                            width: "100%",
                                            padding: "6px 8px",
                                            border: "1px solid #e5e7eb",
                                            borderRadius: "4px",
                                            fontSize: "14px",
                                          }}
                                        />
                                      </td>
                                      <td
                                        style={{
                                          padding: "8px",
                                          textAlign: "center",
                                        }}
                                      >
                                        <button
                                          onClick={() =>
                                            removeScaleLine(scaleType, line.id)
                                          }
                                          disabled={saving}
                                          style={{
                                            background: "transparent",
                                            border: "none",
                                            color: "#dc2626",
                                            cursor: "pointer",
                                            padding: "4px",
                                          }}
                                        >
                                          ×
                                        </button>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                              <button
                                onClick={() => addScaleLine(scaleType)}
                                disabled={saving}
                                style={{
                                  marginTop: "12px",
                                  padding: "6px 12px",
                                  background: "transparent",
                                  border: "1px solid #e5e7eb",
                                  borderRadius: "4px",
                                  fontSize: "13px",
                                  cursor: "pointer",
                                  color: "#6b7280",
                                }}
                              >
                                + Add Row
                              </button>
                            </>
                          ) : (
                            <p
                              style={{
                                fontSize: "14px",
                                color: "#6b7280",
                                fontStyle: "italic",
                              }}
                            >
                              Click "+ Add Row" to add manager pay scale lines
                            </p>
                          )}
                          <button
                            onClick={() => addScaleLine(scaleType)}
                            disabled={saving}
                            style={{
                              marginTop: "12px",
                              padding: "6px 12px",
                              background: "transparent",
                              border: "1px solid #e5e7eb",
                              borderRadius: "4px",
                              fontSize: "13px",
                              cursor: "pointer",
                              color: "#6b7280",
                            }}
                          >
                            + Add Row
                          </button>
                        </div>
                      );
                    })()}
                  </div>
                )}
              </>
            )}

            {/* ── Production Bonuses + Peer Overrides (read view) ── */}
            {!isEditing &&
              showCompensation &&
              (compensation?.production_bonuses?.length > 0 ||
                compensation?.peer_overrides?.length > 0) && (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
                    gap: "24px",
                    marginTop: "20px",
                    marginBottom: "20px",
                  }}
                >
                  {/* Production Bonuses card */}
                  {compensation?.production_bonuses?.length > 0 && (
                    <div
                      style={{
                        border: "1px solid #e5e7eb",
                        borderRadius: "8px",
                        padding: "16px",
                        background: "white",
                      }}
                    >
                      <h4 style={{ marginTop: 0, marginBottom: "12px" }}>
                        Production Bonuses
                      </h4>
                      <table
                        style={{ width: "100%", borderCollapse: "collapse" }}
                      >
                        <thead>
                          <tr>
                            <th
                              style={{
                                textAlign: "left",
                                padding: "8px",
                                borderBottom: "2px solid #e5e7eb",
                                fontSize: "12px",
                                fontWeight: "600",
                                color: "#6b7280",
                              }}
                            >
                              Production Level ($)
                            </th>
                            <th
                              style={{
                                textAlign: "left",
                                padding: "8px",
                                borderBottom: "2px solid #e5e7eb",
                                fontSize: "12px",
                                fontWeight: "600",
                                color: "#6b7280",
                              }}
                            >
                              Cash Bonus ($)
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {compensation.production_bonuses.map((b, i) => (
                            <tr key={i}>
                              <td style={{ padding: "8px", fontSize: "14px" }}>
                                ${Number(b.revenue_amount).toLocaleString()}
                              </td>
                              <td
                                style={{
                                  padding: "8px",
                                  fontSize: "14px",
                                  color: "#16a34a",
                                  fontWeight: "600",
                                }}
                              >
                                ${Number(b.bonus_amount).toLocaleString()}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Custom Peer Overrides card */}
                  {compensation?.peer_overrides?.length > 0 && (
                    <div
                      style={{
                        border: "1px solid #e5e7eb",
                        borderRadius: "8px",
                        padding: "16px",
                        background: "white",
                      }}
                    >
                      <h4 style={{ marginTop: 0, marginBottom: "12px" }}>
                        Custom Peer Overrides
                      </h4>
                      {compensation.peer_overrides.map((o, i) => (
                        <div
                          key={i}
                          style={{
                            marginBottom: "16px",
                            borderBottom:
                              i < compensation.peer_overrides.length - 1
                                ? "1px solid #f3f4f6"
                                : "none",
                            paddingBottom:
                              i < compensation.peer_overrides.length - 1
                                ? "16px"
                                : "0",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                              marginBottom: "8px",
                            }}
                          >
                            <span
                              style={{ fontSize: "13px", fontWeight: "600" }}
                            >
                              {o.role === "grantor"
                                ? `${o.recipient_name} earns from ${rep?.first_name}'s production`
                                : `You earn from ${rep?.first_name}'s production`}
                            </span>
                            {o.label && (
                              <span
                                style={{
                                  fontSize: "12px",
                                  color: "#6b7280",
                                  background: "#f3f4f6",
                                  padding: "2px 8px",
                                  borderRadius: "12px",
                                }}
                              >
                                {o.label}
                              </span>
                            )}
                            {o.role === "recipient" && (
                              <span
                                style={{
                                  fontSize: "11px",
                                  color: "#2563eb",
                                  background: "#dbeafe",
                                  padding: "2px 8px",
                                  borderRadius: "12px",
                                }}
                              >
                                Granted to you
                              </span>
                            )}
                          </div>
                          <table
                            style={{
                              width: "100%",
                              borderCollapse: "collapse",
                            }}
                          >
                            <thead>
                              <tr>
                                <th
                                  style={{
                                    textAlign: "left",
                                    padding: "6px 8px",
                                    borderBottom: "1px solid #e5e7eb",
                                    fontSize: "11px",
                                    fontWeight: "600",
                                    color: "#9ca3af",
                                  }}
                                >
                                  Revenue ($)
                                </th>
                                <th
                                  style={{
                                    textAlign: "left",
                                    padding: "6px 8px",
                                    borderBottom: "1px solid #e5e7eb",
                                    fontSize: "11px",
                                    fontWeight: "600",
                                    color: "#9ca3af",
                                  }}
                                >
                                  Override %
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {o.scale_lines.map((l, li) => (
                                <tr key={li}>
                                  <td
                                    style={{
                                      padding: "6px 8px",
                                      fontSize: "13px",
                                    }}
                                  >
                                    ${Number(l.revenue_amount).toLocaleString()}
                                  </td>
                                  <td
                                    style={{
                                      padding: "6px 8px",
                                      fontSize: "13px",
                                      fontWeight: "600",
                                    }}
                                  >
                                    {l.effective_percentage ?? l.percentage}%
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

            {/* ── Custom Peer Overrides + Production Bonuses (edit view) ── */}
            {isEditing && (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
                  gap: "24px",
                  marginTop: "20px",
                  marginBottom: "20px",
                }}
              >
                {/* ── Production Bonuses Card ── */}
                <div
                  style={{
                    border: "1px solid #e5e7eb",
                    borderRadius: "8px",
                    padding: "16px",
                    background: "white",
                  }}
                >
                  <h4 style={{ marginTop: 0, marginBottom: "12px" }}>
                    Production Bonuses
                  </h4>
                  <p
                    style={{
                      fontSize: "13px",
                      color: "#6b7280",
                      marginBottom: "12px",
                    }}
                  >
                    Cash bonuses tied to production levels. These come out of
                    your margin.
                  </p>
                  {editFormData.productionBonuses.length > 0 && (
                    <table
                      style={{
                        width: "100%",
                        borderCollapse: "collapse",
                        marginBottom: "8px",
                      }}
                    >
                      <thead>
                        <tr>
                          <th
                            style={{
                              textAlign: "left",
                              padding: "8px",
                              borderBottom: "2px solid #e5e7eb",
                              fontSize: "12px",
                              fontWeight: "600",
                              color: "#6b7280",
                            }}
                          >
                            Production Level ($)
                          </th>
                          <th
                            style={{
                              textAlign: "left",
                              padding: "8px",
                              borderBottom: "2px solid #e5e7eb",
                              fontSize: "12px",
                              fontWeight: "600",
                              color: "#6b7280",
                            }}
                          >
                            Cash Bonus ($)
                          </th>
                          <th
                            style={{
                              width: "40px",
                              borderBottom: "2px solid #e5e7eb",
                            }}
                          ></th>
                        </tr>
                      </thead>
                      <tbody>
                        {editFormData.productionBonuses.map(b => (
                          <tr key={b.id}>
                            <td style={{ padding: "8px" }}>
                              <input
                                type="number"
                                value={b.revenue_amount}
                                min="0"
                                step="0.01"
                                placeholder="0"
                                onChange={e => {
                                  setDidEditBonusTiers(true);
                                  setEditFormData(prev => ({
                                    ...prev,
                                    productionBonuses:
                                      prev.productionBonuses.map(x =>
                                        x.id === b.id
                                          ? {
                                              ...x,
                                              revenue_amount: e.target.value,
                                            }
                                          : x
                                      ),
                                  }));
                                }}
                                disabled={saving}
                                style={{
                                  width: "100%",
                                  padding: "6px 8px",
                                  border: "1px solid #e5e7eb",
                                  borderRadius: "4px",
                                  fontSize: "14px",
                                }}
                              />
                            </td>
                            <td style={{ padding: "8px" }}>
                              <input
                                type="number"
                                value={b.bonus_amount}
                                min="0"
                                step="0.01"
                                placeholder="0"
                                onChange={e => {
                                  setDidEditBonusTiers(true);
                                  setEditFormData(prev => ({
                                    ...prev,
                                    productionBonuses:
                                      prev.productionBonuses.map(x =>
                                        x.id === b.id
                                          ? {
                                              ...x,
                                              bonus_amount: e.target.value,
                                            }
                                          : x
                                      ),
                                  }));
                                }}
                                disabled={saving}
                                style={{
                                  width: "100%",
                                  padding: "6px 8px",
                                  border: "1px solid #e5e7eb",
                                  borderRadius: "4px",
                                  fontSize: "14px",
                                }}
                              />
                            </td>
                            <td style={{ padding: "8px", textAlign: "center" }}>
                              <button
                                onClick={() => {
                                  setDidEditBonusTiers(true);
                                  setEditFormData(prev => ({
                                    ...prev,
                                    productionBonuses:
                                      prev.productionBonuses.filter(
                                        x => x.id !== b.id
                                      ),
                                  }));
                                }}
                                disabled={saving}
                                style={{
                                  background: "transparent",
                                  border: "none",
                                  color: "#dc2626",
                                  cursor: "pointer",
                                  padding: "4px",
                                }}
                              >
                                ×
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                  <button
                    disabled={saving}
                    onClick={() => {
                      setDidEditBonusTiers(true);
                      setEditFormData(prev => {
                        const newId =
                          prev.productionBonuses.length > 0
                            ? Math.max(
                                ...prev.productionBonuses.map(b => b.id)
                              ) + 1
                            : 1;
                        return {
                          ...prev,
                          productionBonuses: [
                            ...prev.productionBonuses,
                            {
                              id: newId,
                              revenue_amount: "",
                              bonus_amount: "",
                            },
                          ],
                        };
                      });
                    }}
                    style={{
                      padding: "6px 12px",
                      background: "transparent",
                      border: "1px solid #e5e7eb",
                      borderRadius: "4px",
                      fontSize: "13px",
                      cursor: "pointer",
                      color: "#6b7280",
                    }}
                  >
                    + Add Bonus Tier
                  </button>
                </div>
                {/* ── Custom Peer Overrides Card ── */}
                <div
                  style={{
                    border: "1px solid #e5e7eb",
                    borderRadius: "8px",
                    padding: "16px",
                    background: "white",
                  }}
                >
                  <h4 style={{ marginTop: 0, marginBottom: "4px" }}>
                    Custom Peer Overrides
                  </h4>
                  <p
                    style={{
                      fontSize: "13px",
                      color: "#6b7280",
                      marginBottom: "16px",
                    }}
                  >
                    {rep?.first_name} is the <strong>production source</strong>{" "}
                    — whoever you pick as the receiver will earn an override %
                    on {rep?.first_name}'s revenue. The cost comes out of the
                    grantor's margin.
                  </p>
                  {editFormData.peerOverrides.map(o => (
                    <div
                      key={o.id}
                      style={{
                        marginBottom: "20px",
                        border: "1px solid #e5e7eb",
                        borderRadius: "8px",
                        padding: "14px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          gap: "12px",
                          alignItems: "flex-start",
                          marginBottom: "10px",
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <label
                            style={{
                              fontSize: "12px",
                              fontWeight: "600",
                              color: "#374151",
                              display: "block",
                              marginBottom: "4px",
                            }}
                          >
                            Grantor
                          </label>
                          <select
                            value={o.grantor_user_season_id}
                            disabled={saving}
                            onChange={e => {
                              setDidEditPeerOverrides(true);
                              setEditFormData(prev => ({
                                ...prev,
                                peerOverrides: prev.peerOverrides.map(x =>
                                  x.id === o.id
                                    ? {
                                        ...x,
                                        grantor_user_season_id: e.target.value,
                                      }
                                    : x
                                ),
                              }));
                            }}
                            style={{
                              width: "100%",
                              padding: "8px 10px",
                              border: "1px solid #e5e7eb",
                              borderRadius: "6px",
                              fontSize: "13px",
                              backgroundColor: "white",
                            }}
                          >
                            <option value="">You (default)</option>
                            {(() => {
                              // Build upline chain: walk from Jerry's direct manager up
                              // through manager_id links.  Stop when we hit a manager_id
                              // that isn't in the reps list (= above the logged-in user,
                              // e.g. Landon is not visible to Zack).
                              // Result: only Zack, John, Jim appear — not Landon.
                              const repsByUserSeasonId = new Map(
                                reps.map(r => [r.user_season_id, r])
                              );
                              const grantorChain = [];
                              const visited = new Set();
                              let currentId = rep?.manager_id;
                              while (currentId && !visited.has(currentId)) {
                                visited.add(currentId);
                                const currentRep =
                                  repsByUserSeasonId.get(currentId);
                                if (!currentRep) break; // above logged-in user's scope
                                grantorChain.push(currentRep);
                                currentId = currentRep.manager_id;
                              }
                              // Exclude the logged-in user from the explicit options — they are
                              // already represented by the "You (default)" empty-string option.
                              // The logged-in user is the rep whose manager_id points outside
                              // the reps list (i.e., to someone above the logged-in user).
                              return grantorChain
                                .filter(
                                  r =>
                                    r.manager_id &&
                                    repsByUserSeasonId.has(r.manager_id)
                                )
                                .map(r => (
                                  <option
                                    key={r.user_season_id}
                                    value={r.user_season_id}
                                  >
                                    {r.first_name} {r.last_name}
                                  </option>
                                ));
                            })()}
                          </select>
                        </div>
                        <div style={{ flex: 1 }}>
                          <label
                            style={{
                              fontSize: "12px",
                              fontWeight: "600",
                              color: "#374151",
                              display: "block",
                              marginBottom: "4px",
                            }}
                          >
                            Receiver
                          </label>
                          <select
                            value={o.recipient_user_season_id}
                            disabled={saving}
                            onChange={e => {
                              setDidEditPeerOverrides(true);
                              setEditFormData(prev => ({
                                ...prev,
                                peerOverrides: prev.peerOverrides.map(x =>
                                  x.id === o.id
                                    ? {
                                        ...x,
                                        recipient_user_season_id:
                                          e.target.value,
                                      }
                                    : x
                                ),
                              }));
                            }}
                            style={{
                              width: "100%",
                              padding: "8px 10px",
                              border:
                                o.recipient_user_season_id === ""
                                  ? "1px solid #fca5a5"
                                  : "1px solid #e5e7eb",
                              borderRadius: "6px",
                              fontSize: "13px",
                              backgroundColor: "white",
                            }}
                          >
                            <option value="">Select a rep...</option>
                            {reps
                              .filter(
                                r =>
                                  r.user_season_id && r.rep_id !== rep?.rep_id
                              )
                              .map(r => (
                                <option
                                  key={r.user_season_id}
                                  value={r.user_season_id}
                                >
                                  {r.first_name} {r.last_name}
                                </option>
                              ))}
                          </select>
                        </div>
                        <button
                          onClick={() => {
                            setDidEditPeerOverrides(true);
                            setEditFormData(prev => ({
                              ...prev,
                              peerOverrides: prev.peerOverrides.filter(
                                x => x.id !== o.id
                              ),
                            }));
                          }}
                          disabled={saving}
                          style={{
                            background: "transparent",
                            border: "none",
                            color: "#dc2626",
                            cursor: "pointer",
                            padding: "4px",
                            marginTop: "20px",
                            fontSize: "18px",
                          }}
                        >
                          ×
                        </button>
                      </div>
                      <table
                        style={{ width: "100%", borderCollapse: "collapse" }}
                      >
                        <thead>
                          <tr>
                            <th
                              style={{
                                textAlign: "left",
                                padding: "6px 8px",
                                borderBottom: "1px solid #e5e7eb",
                                fontSize: "11px",
                                fontWeight: "600",
                                color: "#9ca3af",
                              }}
                            >
                              Condition
                            </th>
                            <th
                              style={{
                                textAlign: "left",
                                padding: "6px 8px",
                                borderBottom: "1px solid #e5e7eb",
                                fontSize: "11px",
                                fontWeight: "600",
                                color: "#9ca3af",
                              }}
                            >
                              Revenue Threshold ($)
                            </th>
                            <th
                              style={{
                                textAlign: "left",
                                padding: "6px 8px",
                                borderBottom: "1px solid #e5e7eb",
                                fontSize: "11px",
                                fontWeight: "600",
                                color: "#9ca3af",
                              }}
                            >
                              Override %
                            </th>
                            <th
                              style={{
                                width: "32px",
                                borderBottom: "1px solid #e5e7eb",
                              }}
                            ></th>
                          </tr>
                        </thead>
                        <tbody>
                          {o.scale_lines.map(l => (
                            <tr key={l.id}>
                              <td style={{ padding: "6px 8px" }}>
                                <select
                                  value={l.condition_type || "REVENUE_GTE"}
                                  disabled={saving}
                                  onChange={e => {
                                    setDidEditPeerOverrides(true);
                                    setEditFormData(prev => ({
                                      ...prev,
                                      peerOverrides: prev.peerOverrides.map(
                                        x =>
                                          x.id === o.id
                                            ? {
                                                ...x,
                                                scale_lines: x.scale_lines.map(
                                                  sl =>
                                                    sl.id === l.id
                                                      ? {
                                                          ...sl,
                                                          condition_type:
                                                            e.target.value,
                                                        }
                                                      : sl
                                                ),
                                              }
                                            : x
                                      ),
                                    }));
                                  }}
                                  style={{
                                    width: "100%",
                                    minWidth:
                                      (l.condition_type || "REVENUE_GTE") ===
                                      "MARGIN_GUARANTEE"
                                        ? "160px"
                                        : undefined,
                                    padding: "5px 6px",
                                    border: "1px solid #e5e7eb",
                                    borderRadius: "4px",
                                    fontSize: "12px",
                                    backgroundColor: "white",
                                  }}
                                >
                                  <option value="REVENUE_GTE">Revenue ≥</option>
                                  <option value="REVENUE_LT">
                                    Revenue &lt;
                                  </option>
                                  <option value="MARGIN_GUARANTEE">
                                    Minimum Override
                                  </option>
                                </select>
                              </td>
                              <td style={{ padding: "6px 8px" }}>
                                <input
                                  type="number"
                                  value={l.revenue_amount}
                                  min="0"
                                  step="0.01"
                                  placeholder="0"
                                  onChange={e => {
                                    setDidEditPeerOverrides(true);
                                    setEditFormData(prev => ({
                                      ...prev,
                                      peerOverrides: prev.peerOverrides.map(
                                        x =>
                                          x.id === o.id
                                            ? {
                                                ...x,
                                                scale_lines: x.scale_lines.map(
                                                  sl =>
                                                    sl.id === l.id
                                                      ? {
                                                          ...sl,
                                                          revenue_amount:
                                                            e.target.value,
                                                        }
                                                      : sl
                                                ),
                                              }
                                            : x
                                      ),
                                    }));
                                  }}
                                  disabled={
                                    saving ||
                                    (l.condition_type || "REVENUE_GTE") ===
                                      "MARGIN_GUARANTEE"
                                  }
                                  style={{
                                    width: "100%",
                                    padding: "5px 8px",
                                    border: "1px solid #e5e7eb",
                                    borderRadius: "4px",
                                    fontSize: "13px",
                                    opacity:
                                      (l.condition_type || "REVENUE_GTE") ===
                                      "MARGIN_GUARANTEE"
                                        ? 0.3
                                        : 1,
                                  }}
                                />
                              </td>
                              <td style={{ padding: "6px 8px" }}>
                                <input
                                  type="number"
                                  value={l.percentage}
                                  min="0"
                                  max="100"
                                  step="0.01"
                                  placeholder="0"
                                  onChange={e => {
                                    setDidEditPeerOverrides(true);
                                    setEditFormData(prev => ({
                                      ...prev,
                                      peerOverrides: prev.peerOverrides.map(
                                        x =>
                                          x.id === o.id
                                            ? {
                                                ...x,
                                                scale_lines: x.scale_lines.map(
                                                  sl =>
                                                    sl.id === l.id
                                                      ? {
                                                          ...sl,
                                                          percentage:
                                                            e.target.value,
                                                        }
                                                      : sl
                                                ),
                                              }
                                            : x
                                      ),
                                    }));
                                  }}
                                  disabled={saving}
                                  style={{
                                    width: "100%",
                                    padding: "5px 8px",
                                    border: "1px solid #e5e7eb",
                                    borderRadius: "4px",
                                    fontSize: "13px",
                                  }}
                                />
                              </td>
                              <td
                                style={{
                                  padding: "6px 8px",
                                  textAlign: "center",
                                }}
                              >
                                <button
                                  onClick={() => {
                                    setDidEditPeerOverrides(true);
                                    setEditFormData(prev => ({
                                      ...prev,
                                      peerOverrides: prev.peerOverrides.map(
                                        x =>
                                          x.id === o.id
                                            ? {
                                                ...x,
                                                scale_lines:
                                                  x.scale_lines.filter(
                                                    sl => sl.id !== l.id
                                                  ),
                                              }
                                            : x
                                      ),
                                    }));
                                  }}
                                  disabled={saving || o.scale_lines.length <= 1}
                                  style={{
                                    background: "transparent",
                                    border: "none",
                                    color: "#dc2626",
                                    cursor:
                                      o.scale_lines.length <= 1
                                        ? "not-allowed"
                                        : "pointer",
                                    opacity:
                                      o.scale_lines.length <= 1 ? 0.4 : 1,
                                  }}
                                >
                                  ×
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {o.scale_lines.some(
                        sl =>
                          (sl.condition_type || "REVENUE_GTE") ===
                          "MARGIN_GUARANTEE"
                      ) ? (
                        <p
                          style={{
                            marginTop: "8px",
                            fontSize: "12px",
                            color: "#6b7280",
                            fontStyle: "italic",
                            lineHeight: "1.4",
                          }}
                        >
                          The receiver will get at least this override % even if
                          their expenses exceed their margin.
                        </p>
                      ) : (
                        <button
                          disabled={saving}
                          onClick={() => {
                            setDidEditPeerOverrides(true);
                            setEditFormData(prev => ({
                              ...prev,
                              peerOverrides: prev.peerOverrides.map(x =>
                                x.id === o.id
                                  ? {
                                      ...x,
                                      scale_lines: [
                                        ...x.scale_lines,
                                        {
                                          id:
                                            Math.max(
                                              ...x.scale_lines.map(sl => sl.id)
                                            ) + 1,
                                          revenue_amount: "",
                                          percentage: "",
                                          condition_type: "REVENUE_GTE",
                                        },
                                      ],
                                    }
                                  : x
                              ),
                            }));
                          }}
                          style={{
                            marginTop: "8px",
                            padding: "4px 10px",
                            background: "transparent",
                            border: "1px solid #e5e7eb",
                            borderRadius: "4px",
                            fontSize: "12px",
                            cursor: "pointer",
                            color: "#6b7280",
                          }}
                        >
                          + Add Row
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    disabled={saving}
                    onClick={() => {
                      setDidEditPeerOverrides(true);
                      setEditFormData(prev => {
                        const newId =
                          prev.peerOverrides.length > 0
                            ? Math.max(...prev.peerOverrides.map(o => o.id)) + 1
                            : 1;
                        return {
                          ...prev,
                          peerOverrides: [
                            ...prev.peerOverrides,
                            {
                              id: newId,
                              grantor_user_season_id: "",
                              recipient_user_season_id: "",
                              recipient_name: "",
                              label: "",
                              scale_lines: [
                                {
                                  id: 1,
                                  revenue_amount: "",
                                  percentage: "",
                                  condition_type: "REVENUE_GTE",
                                },
                              ],
                            },
                          ],
                        };
                      });
                    }}
                    style={{
                      padding: "6px 12px",
                      background: "transparent",
                      border: "1px solid #e5e7eb",
                      borderRadius: "4px",
                      fontSize: "13px",
                      cursor: "pointer",
                      color: "#6b7280",
                    }}
                  >
                    + Add Peer Override
                  </button>
                </div>
              </div>
            )}

            {/* Notes Section */}
            {rep.notes && (
              <>
                <h2 style={{ marginBottom: "24px", marginTop: "40px" }}>
                  <IconUser />
                  Notes
                </h2>
                <div className="rep-details-notes-content">
                  <p>{rep.notes}</p>
                </div>
              </>
            )}

            {/* Bottom Save/Cancel Buttons in Edit Mode */}
            {isEditing && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  gap: "12px",
                  marginTop: "40px",
                  paddingTop: "24px",
                  borderTop: "1px solid #e5e7eb",
                }}
              >
                <button
                  onClick={() => setShowCancelConfirmModal(true)}
                  disabled={saving}
                  style={{
                    padding: "12px 32px",
                    background: "white",
                    border: "1px solid #e5e7eb",
                    borderRadius: "8px",
                    fontSize: "15px",
                    fontWeight: "500",
                    cursor: "pointer",
                    color: "#6b7280",
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={() => setShowSaveConfirmModal(true)}
                  disabled={saving}
                  style={{
                    padding: "12px 32px",
                    background: "#ff6b35",
                    border: "none",
                    borderRadius: "8px",
                    fontSize: "15px",
                    fontWeight: "500",
                    cursor: "pointer",
                    color: "white",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  {saving ? <Loader size="sm" color="white" /> : null}
                  {saving ? "Saving..." : "Save Changes"}
                </button>
              </div>
            )}
          </div>
        )}

        {/* History Section */}
        {activeTab === "history" && (
          <div className="rep-details-info-section" style={{ padding: "24px" }}>
            {isLoadingHistory ? (
              <div style={{ textAlign: "center", padding: "40px" }}>
                <Loader size="lg" />
                <p style={{ marginTop: "16px", color: "#6b7280" }}>
                  Loading history...
                </p>
              </div>
            ) : history.length === 0 ? (
              <div style={{ textAlign: "center", padding: "40px" }}>
                <IconClock
                  size={48}
                  color="#9ca3af"
                  style={{ marginBottom: "16px" }}
                />
                <h3
                  style={{
                    color: "#6b7280",
                    fontSize: "18px",
                    marginBottom: "8px",
                  }}
                >
                  No History Yet
                </h3>
                <p style={{ color: "#9ca3af", fontSize: "14px" }}>
                  Activity history will appear here once available
                </p>
              </div>
            ) : (
              <div>
                <h2
                  style={{
                    marginBottom: "24px",
                    fontSize: "20px",
                    fontWeight: "600",
                  }}
                >
                  Activity History
                </h2>
                <div>
                  {history.map((item, index) => (
                    <div
                      key={item.id || index}
                      style={{
                        padding: "16px",
                        marginBottom: "12px",
                        background: "white",
                        borderRadius: "8px",
                        border: "1px solid #e5e7eb",
                        display: "flex",
                        gap: "14px",
                        alignItems: "flex-start",
                      }}
                    >
                      <div
                        style={{
                          width: "36px",
                          height: "36px",
                          borderRadius: "999px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          background: getHistoryAccent(item.source).bg,
                          color: getHistoryAccent(item.source).color,
                          flexShrink: 0,
                        }}
                      >
                        {getHistoryIcon(item.source)}
                      </div>

                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            gap: "12px",
                            flexWrap: "wrap",
                            marginBottom: "6px",
                          }}
                        >
                          <div>
                            <div
                              style={{
                                fontSize: "15px",
                                fontWeight: "600",
                                color: "#111827",
                                marginBottom: "4px",
                              }}
                            >
                              {item.event_type || "History Event"}
                            </div>
                            <div
                              style={{
                                display: "flex",
                                gap: "8px",
                                flexWrap: "wrap",
                                alignItems: "center",
                              }}
                            >
                              <span
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  padding: "2px 8px",
                                  borderRadius: "999px",
                                  fontSize: "11px",
                                  fontWeight: "700",
                                  letterSpacing: "0.02em",
                                  background: getHistoryAccent(item.source).bg,
                                  color: getHistoryAccent(item.source).color,
                                }}
                              >
                                {getHistoryAccent(item.source).label}
                              </span>
                              {item.timeline_group === "pre-conversion" && (
                                <span
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    padding: "2px 8px",
                                    borderRadius: "999px",
                                    fontSize: "11px",
                                    fontWeight: "700",
                                    background: "#fef3c7",
                                    color: "#b45309",
                                  }}
                                >
                                  Before Becoming a Rep
                                </span>
                              )}
                            </div>
                          </div>

                          <div
                            style={{
                              fontSize: "12px",
                              color: "#6b7280",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {formatHistoryDate(
                              item.event_date || item.created_at
                            )}
                          </div>
                        </div>

                        {item.metadata?.title &&
                        item.metadata.title !== item.notes &&
                        item.metadata.title !== item.event_type ? (
                          <div
                            style={{
                              fontSize: "14px",
                              fontWeight: "600",
                              color: "#374151",
                              marginBottom: item.notes ? "6px" : "0",
                            }}
                          >
                            {item.metadata.title}
                          </div>
                        ) : null}

                        {item.notes ? (
                          <p
                            style={{
                              margin: 0,
                              color: "#4b5563",
                              fontSize: "14px",
                              lineHeight: 1.5,
                            }}
                          >
                            {item.notes}
                          </p>
                        ) : null}

                        {(item.location ||
                          item.status ||
                          item.document_title) && (
                          <div
                            style={{
                              display: "flex",
                              gap: "12px",
                              flexWrap: "wrap",
                              marginTop: "8px",
                              fontSize: "12px",
                              color: "#6b7280",
                            }}
                          >
                            {item.location ? (
                              <span>Location: {item.location}</span>
                            ) : null}
                            {item.status ? (
                              <span>
                                Status: {String(item.status).replace(/_/g, " ")}
                              </span>
                            ) : null}
                            {item.document_title ? (
                              <span>Document: {item.document_title}</span>
                            ) : null}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Deal Section */}
        {activeTab === "deal" && (
          <div className="rep-details-info-section">
            <RepDealTab
              repId={id}
              repName={
                rep
                  ? `${rep.first_name || ""} ${rep.last_name || ""}`.trim()
                  : ""
              }
            />
          </div>
        )}

        {/* Documents Section */}
        {activeTab === "documents" && (
          <div className="rep-details-info-section" style={{ padding: "24px" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "24px",
              }}
            >
              <h2 style={{ margin: 0 }}>Documents</h2>
              <button
                onClick={() => setIsUploadModalOpen(true)}
                style={{
                  padding: "10px 20px",
                  background: "#ff6b35",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                + Upload Document
              </button>
            </div>

            {documentsLoading && (
              <div style={{ textAlign: "center", padding: "40px" }}>
                <Loader size="lg" />
                <p style={{ marginTop: "16px", color: "#6b7280" }}>
                  Loading documents...
                </p>
              </div>
            )}

            {documentsError && (
              <Alert
                icon={<IconAlertCircle size={16} />}
                color="red"
                variant="light"
                mb="md"
              >
                {documentsError}
              </Alert>
            )}

            {!documentsLoading && repDocuments && (
              <>
                {/* E-Signed Documents */}
                <div style={{ marginBottom: "32px" }}>
                  <h3
                    style={{
                      fontSize: "15px",
                      fontWeight: "600",
                      marginBottom: "12px",
                      color: "#374151",
                    }}
                  >
                    System Documents
                  </h3>
                  {repDocuments.signed_documents.length === 0 ? (
                    <p
                      style={{
                        color: "#9ca3af",
                        fontSize: "14px",
                        fontStyle: "italic",
                      }}
                    >
                      No signed documents yet.
                    </p>
                  ) : (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "8px",
                      }}
                    >
                      {repDocuments.signed_documents.map(doc => (
                        <div
                          key={doc.id}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            padding: "14px 16px",
                            background: "white",
                            border: "1px solid #e5e7eb",
                            borderRadius: "8px",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "12px",
                            }}
                          >
                            <div
                              style={{
                                width: "36px",
                                height: "36px",
                                background: "#fee2e2",
                                borderRadius: "8px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "14px",
                                fontWeight: "700",
                                color: "#dc2626",
                              }}
                            >
                              PDF
                            </div>
                            <div>
                              <p
                                style={{
                                  margin: 0,
                                  fontWeight: "600",
                                  fontSize: "14px",
                                }}
                              >
                                {doc.document_name || "Untitled Document"}
                              </p>
                              <p
                                style={{
                                  margin: 0,
                                  fontSize: "12px",
                                  color: "#6b7280",
                                }}
                              >
                                {new Date(doc.created_at).toLocaleDateString(
                                  "en-US",
                                  {
                                    year: "numeric",
                                    month: "short",
                                    day: "numeric",
                                  }
                                )}
                                {" · "}
                                <span
                                  style={{
                                    display: "inline-block",
                                    padding: "1px 8px",
                                    borderRadius: "12px",
                                    fontSize: "11px",
                                    fontWeight: "600",
                                    background:
                                      doc.status === "completed"
                                        ? "#d1fae5"
                                        : doc.status === "voided"
                                          ? "#fee2e2"
                                          : "#fef3c7",
                                    color:
                                      doc.status === "completed"
                                        ? "#065f46"
                                        : doc.status === "voided"
                                          ? "#991b1b"
                                          : "#92400e",
                                  }}
                                >
                                  {doc.status?.replace(/_/g, " ").toUpperCase()}
                                </span>
                              </p>
                            </div>
                          </div>
                          {doc.completed_pdf_url && (
                            <a
                              href={doc.completed_pdf_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                padding: "6px 14px",
                                border: "1px solid #e5e7eb",
                                borderRadius: "6px",
                                fontSize: "13px",
                                color: "#374151",
                                textDecoration: "none",
                                background: "white",
                              }}
                            >
                              View
                            </a>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Uploaded Files */}
                <div>
                  <h3
                    style={{
                      fontSize: "15px",
                      fontWeight: "600",
                      marginBottom: "12px",
                      color: "#374151",
                    }}
                  >
                    Uploaded Files
                  </h3>
                  {repDocuments.uploaded_files.length === 0 ? (
                    <p
                      style={{
                        color: "#9ca3af",
                        fontSize: "14px",
                        fontStyle: "italic",
                      }}
                    >
                      No uploaded files yet. Click "Upload Document" to add
                      files.
                    </p>
                  ) : (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "8px",
                      }}
                    >
                      {repDocuments.uploaded_files.map(file => (
                        <div
                          key={file.id}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            padding: "14px 16px",
                            background: "white",
                            border: "1px solid #e5e7eb",
                            borderRadius: "8px",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "12px",
                            }}
                          >
                            <div
                              style={{
                                width: "36px",
                                height: "36px",
                                background: "#ede9fe",
                                borderRadius: "8px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "14px",
                                fontWeight: "700",
                                color: "#7c3aed",
                              }}
                            >
                              PDF
                            </div>
                            <div>
                              <p
                                style={{
                                  margin: 0,
                                  fontWeight: "600",
                                  fontSize: "14px",
                                }}
                              >
                                {file.label}
                              </p>
                              <p
                                style={{
                                  margin: 0,
                                  fontSize: "12px",
                                  color: "#6b7280",
                                }}
                              >
                                {new Date(file.created_at).toLocaleDateString(
                                  "en-US",
                                  {
                                    year: "numeric",
                                    month: "short",
                                    day: "numeric",
                                  }
                                )}
                                {file.file_size
                                  ? ` · ${(file.file_size / 1024).toFixed(0)} KB`
                                  : ""}
                              </p>
                            </div>
                          </div>
                          <div style={{ display: "flex", gap: "8px" }}>
                            <a
                              href={file.s3_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                padding: "6px 14px",
                                border: "1px solid #e5e7eb",
                                borderRadius: "6px",
                                fontSize: "13px",
                                color: "#374151",
                                textDecoration: "none",
                                background: "white",
                              }}
                            >
                              View
                            </a>
                            <button
                              onClick={() => handleDeleteAttachment(file.id)}
                              style={{
                                padding: "6px 14px",
                                border: "1px solid #fca5a5",
                                borderRadius: "6px",
                                fontSize: "13px",
                                color: "#dc2626",
                                background: "white",
                                cursor: "pointer",
                              }}
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {/* Upload Document Modal */}
        <Modal
          opened={isUploadModalOpen}
          onClose={() => {
            setIsUploadModalOpen(false);
            setUploadFile(null);
            setUploadLabel("");
          }}
          title="Upload Document"
          centered
          size="md"
        >
          <Stack gap="md">
            <div>
              <Text size="sm" fw={600} mb={4}>
                Document Name / Label
              </Text>
              <input
                type="text"
                value={uploadLabel}
                onChange={e => setUploadLabel(e.target.value)}
                placeholder="e.g. Contract 2025, Background Check"
                disabled={uploading}
                style={{
                  width: "100%",
                  padding: "8px 12px",
                  border: "1px solid #e5e7eb",
                  borderRadius: "6px",
                  fontSize: "14px",
                  boxSizing: "border-box",
                }}
              />
            </div>
            <div>
              <Text size="sm" fw={600} mb={4}>
                File (PDF)
              </Text>
              <input
                type="file"
                accept=".pdf,application/pdf"
                disabled={uploading}
                onChange={e => setUploadFile(e.target.files?.[0] || null)}
                style={{ fontSize: "14px" }}
              />
              {uploadFile && (
                <Text size="xs" c="dimmed" mt={4}>
                  {uploadFile.name} ({(uploadFile.size / 1024).toFixed(0)} KB)
                </Text>
              )}
            </div>
            <Group justify="flex-end" gap="sm" pt="md">
              <Button
                variant="outline"
                onClick={() => {
                  setIsUploadModalOpen(false);
                  setUploadFile(null);
                  setUploadLabel("");
                }}
                disabled={uploading}
              >
                Cancel
              </Button>
              <Button
                onClick={handleUploadDocument}
                loading={uploading}
                disabled={!uploadFile || uploading}
                style={{ background: "#ff6b35" }}
              >
                Upload
              </Button>
            </Group>
          </Stack>
        </Modal>

        <EditRepInfoModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          rep={rep}
          reps={reps}
          repTypes={repTypes}
          recruitingSourceTypes={recruitingSourceTypes}
          onUpdate={handleRepUpdate}
        />

        <EditScalesModal
          isOpen={editScaleType !== null}
          onClose={() => setEditScaleType(null)}
          rep={rep}
          scaleType={editScaleType}
          onUpdate={newLines => {
            // Map scaleType to the compensation/rep state key
            const scaleDataKeyMap = {
              commission_scale: "commission_scale_lines",
              override_scale: "override_scale_lines",
              marketing_deal_scale: "md_scale_lines",
            };
            const dataKey = scaleDataKeyMap[editScaleType];
            if (dataKey) {
              // Update rep state in-place so EditScalesModal re-seeds correctly on next open
              setRep(prev =>
                prev ? { ...prev, [dataKey]: newLines || [] } : prev
              );
              // Update compensation state in-place so ScalesPreview refreshes immediately
              setCompensation(prev =>
                prev ? { ...prev, [dataKey]: newLines || [] } : prev
              );
            }
            // Refresh basic rep details in background (no PIN required)
            fetchRepDetails(true);
          }}
        />

        <DeactivateRepModal
          isOpen={isDeactivateModalOpen}
          onClose={() => setIsDeactivateModalOpen(false)}
          rep={rep}
          onConfirmDeactivate={() => {
            setIsDeactivateModalOpen(false);
            if (user?.role === "COMPANY_ADMIN") {
              navigate("/dashboard");
            } else {
              navigate("/reps");
            }
          }}
        />

        <EditProjectedRevenueModal
          isOpen={isEditProjectedRevenueModalOpen}
          onClose={() => setIsEditProjectedRevenueModalOpen(false)}
          rep={{
            rep_id: rep?.rep_id,
            projected_revenue: compensation?.projected_revenue,
          }}
          onUpdate={newProjectedRevenue => {
            // Update the compensation state with the new projected revenue value
            if (compensation) {
              setCompensation({
                ...compensation,
                projected_revenue: newProjectedRevenue,
              });
            }
          }}
        />

        <Modal
          opened={isPinModalOpen}
          onClose={handleClosePinModal}
          title={
            <Group gap="xs">
              <IconLock size={20} />
              <Title order={3} fw={600}>
                Verify Security PIN
              </Title>
            </Group>
          }
          size="md"
          padding="xl"
          closeOnClickOutside={true}
          closeOnEscape={true}
        >
          <Stack gap="md">
            {pinError && (
              <Alert
                icon={<IconAlertCircle size={16} />}
                title="Error"
                color="red"
                variant="light"
              >
                {pinError}
              </Alert>
            )}

            <Alert
              icon={<IconInfoCircle size={16} />}
              title="Secure Information"
              color="blue"
              variant="light"
            >
              Compensation details are protected. Please enter your 4-digit
              security PIN to view projected revenue, commission scales,
              override scales, and marketing deal information.
            </Alert>

            <Divider />

            <Stack gap="md" align="center">
              <Group gap="xs">
                <IconShieldCheck size={20} />
                <Text fw={600} size="sm">
                  Enter Your PIN
                </Text>
              </Group>
              <PinInput
                length={4}
                value={pin}
                onChange={setPin}
                onComplete={handlePinComplete}
                placeholder="○"
                type="number"
                size="xl"
                disabled={pinVerifying}
                mask
                styles={{
                  input: {
                    borderColor: pinError ? "#fa5252" : undefined,
                  },
                }}
              />
              {pinVerifying && (
                <Group gap="xs">
                  <Loader size="sm" />
                  <Text size="sm" c="dimmed">
                    Verifying PIN...
                  </Text>
                </Group>
              )}
            </Stack>

            <Select
              label="Keep unlocked for"
              value={unlockDuration}
              onChange={value =>
                setUnlockDuration(value || String(DEFAULT_PIN_UNLOCK_DURATION))
              }
              data={PIN_UNLOCK_DURATIONS.map(option => ({
                value: String(option.value),
                label: option.label,
              }))}
              allowDeselect={false}
            />

            <Group justify="space-between" gap="sm" pt="md">
              <Button
                variant="outline"
                onClick={handleClosePinModal}
                disabled={pinVerifying}
                size="sm"
              >
                Cancel
              </Button>
              <Button
                onClick={() => fetchCompensationDetails(pin, pinForEdit)}
                loading={pinVerifying}
                leftSection={
                  pinVerifying ? undefined : <IconShieldCheck size={16} />
                }
                disabled={pin.length !== 4}
                size="sm"
              >
                Verify PIN
              </Button>
            </Group>
          </Stack>
        </Modal>

        {/* Save Confirmation Modal */}
        <Modal
          opened={showSaveConfirmModal}
          onClose={() => setShowSaveConfirmModal(false)}
          title="Confirm Save"
          centered
          size="sm"
        >
          <Stack gap="md">
            <Text>
              Are you sure you want to save these changes to the rep profile?
            </Text>
            <Group justify="flex-end" gap="sm">
              <Button
                variant="outline"
                onClick={() => setShowSaveConfirmModal(false)}
              >
                No, Go Back
              </Button>
              <Button
                style={{ background: "#ff6b35" }}
                onClick={async () => {
                  setShowSaveConfirmModal(false);
                  await handleSaveEdit();
                }}
              >
                Yes, Save Changes
              </Button>
            </Group>
          </Stack>
        </Modal>

        {/* Cancel Confirmation Modal */}
        <Modal
          opened={showCancelConfirmModal}
          onClose={() => setShowCancelConfirmModal(false)}
          title="Confirm Cancel"
          centered
          size="sm"
        >
          <Stack gap="md">
            <Text>
              Are you sure you want to cancel? All unsaved changes will be lost.
            </Text>
            <Group justify="flex-end" gap="sm">
              <Button
                variant="outline"
                onClick={() => setShowCancelConfirmModal(false)}
              >
                No, Keep Editing
              </Button>
              <Button
                color="red"
                onClick={() => {
                  setShowCancelConfirmModal(false);
                  handleCancelEdit();
                }}
              >
                Yes, Discard Changes
              </Button>
            </Group>
          </Stack>
        </Modal>

        {/* Edit Groups Modal */}
        <Modal
          opened={isEditGroupsModalOpen}
          onClose={() => setIsEditGroupsModalOpen(false)}
          title="Edit Groups"
          centered
          size="md"
        >
          <style>
            {`
              .orange-checkbox {
                appearance: none;
                -webkit-appearance: none;
                -moz-appearance: none;
                width: 18px;
                height: 18px;
                border: 2px solid #d1d5db;
                border-radius: 4px;
                cursor: pointer;
                position: relative;
                margin-right: 12px;
                transition: all 0.2s;
              }
              .orange-checkbox:checked {
                background-color: #ff6b35;
                border-color: #ff6b35;
              }
              .orange-checkbox:checked::after {
                content: '✓';
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                color: white;
                font-size: 14px;
                font-weight: bold;
              }
            `}
          </style>
          <Stack gap="md">
            {loadingGroups ? (
              <div style={{ textAlign: "center", padding: "20px" }}>
                <Loader size="md" />
                <Text size="sm" c="dimmed" mt="md">
                  Loading groups...
                </Text>
              </div>
            ) : availableGroups.length === 0 ? (
              <Text c="dimmed" ta="center" py="xl">
                No groups available
              </Text>
            ) : (
              <>
                <Text size="sm" c="dimmed">
                  Select groups to add or remove {rep?.first_name}{" "}
                  {rep?.last_name}:
                </Text>
                <Stack gap="xs">
                  {availableGroups.map(group => (
                    <div
                      key={group.id}
                      onClick={() => toggleGroup(group.id)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "12px",
                        border: "1px solid #e5e7eb",
                        borderRadius: "8px",
                        cursor: "pointer",
                        background: repGroups.includes(group.id)
                          ? "#fff7ed"
                          : "white",
                        borderColor: repGroups.includes(group.id)
                          ? "#ff6b35"
                          : "#e5e7eb",
                        transition: "all 0.2s",
                      }}
                    >
                      <input
                        type="checkbox"
                        className="orange-checkbox"
                        checked={repGroups.includes(group.id)}
                        onChange={() => toggleGroup(group.id)}
                      />
                      <div style={{ flex: 1 }}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          {group.rep_type?.image && (
                            <img
                              src={group.rep_type.image}
                              alt={group.rep_type.name}
                              style={{
                                width: "20px",
                                height: "20px",
                                borderRadius: "50%",
                                objectFit: "cover",
                              }}
                            />
                          )}
                          <Text fw={500} size="sm">
                            {group.name}
                          </Text>
                        </div>
                        {group.manager_name && (
                          <Text size="xs" c="dimmed" mt={4}>
                            Created by {group.manager_name}
                          </Text>
                        )}
                      </div>
                    </div>
                  ))}
                </Stack>
              </>
            )}
            <Group justify="flex-end" gap="sm" mt="md">
              <Button
                variant="outline"
                onClick={() => setIsEditGroupsModalOpen(false)}
                disabled={savingGroups}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveGroups}
                loading={savingGroups}
                style={{ background: "#ff6b35" }}
              >
                Save Changes
              </Button>
            </Group>
          </Stack>
        </Modal>

        {/* Manager Pay Type Selection Modal */}
        <Modal
          opened={isManagerPayTypeSelectionOpen}
          onClose={() => setIsManagerPayTypeSelectionOpen(false)}
          title={
            <Title order={3} fw={600}>
              Select Manager Pay Type
            </Title>
          }
          centered
          size="sm"
        >
          <Stack gap="md">
            <Text size="sm" c="dimmed">
              Choose the type of manager pay scale you want to add for this rep:
            </Text>
            <Stack gap="sm">
              <div
                onClick={() => {
                  setIsManagerPayTypeSelectionOpen(false);
                  setEditScaleType("override_scale");
                }}
                style={{
                  padding: "16px",
                  border: "2px solid #e5e7eb",
                  borderRadius: "8px",
                  cursor: "pointer",
                  transition: "all 0.2s",
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.borderColor = "#ff6b35";
                  e.currentTarget.style.background = "#fff7ed";
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.borderColor = "#e5e7eb";
                  e.currentTarget.style.background = "white";
                }}
              >
                <Text fw={600} size="sm" mb={4}>
                  Override
                </Text>
                <Text size="xs" c="dimmed">
                  Standard override pay scale based on rep revenue thresholds
                </Text>
              </div>
              <div
                onClick={() => {
                  setIsManagerPayTypeSelectionOpen(false);
                  setEditScaleType("marketing_deal_scale");
                }}
                style={{
                  padding: "16px",
                  border: "2px solid #e5e7eb",
                  borderRadius: "8px",
                  cursor: "pointer",
                  transition: "all 0.2s",
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.borderColor = "#ff6b35";
                  e.currentTarget.style.background = "#fff7ed";
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.borderColor = "#e5e7eb";
                  e.currentTarget.style.background = "white";
                }}
              >
                <Text fw={600} size="sm" mb={4}>
                  Marketing Deal
                </Text>
                <Text size="xs" c="dimmed">
                  Marketing deal pay scale for reps with a marketing deal
                  arrangement
                </Text>
              </div>
            </Stack>
            <Group justify="flex-end" mt="xs">
              <Button
                variant="outline"
                onClick={() => setIsManagerPayTypeSelectionOpen(false)}
              >
                Cancel
              </Button>
            </Group>
          </Stack>
        </Modal>

        {/* Delete Confirmation Modal */}
        <Modal
          opened={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          title="Delete Rep Permanently?"
          centered
          size="md"
        >
          <Stack gap="md">
            <Alert
              icon={<IconAlertCircle size={16} />}
              title="Warning: This Action Cannot Be Undone"
              color="red"
              variant="light"
            >
              Deleting this rep will permanently remove all their data from the
              system, including their profile, commission history, training
              records, and all associated information.
            </Alert>
            <Text size="sm">You are about to permanently delete:</Text>
            <div
              style={{
                background: "#f9fafb",
                border: "1px solid #e5e7eb",
                borderRadius: "8px",
                padding: "16px",
              }}
            >
              <Text fw={600} size="md" mb="xs">
                {rep?.first_name} {rep?.last_name}
              </Text>
              <Text size="sm" c="dimmed">
                {rep?.email}
              </Text>
              <Text size="sm" c="dimmed">
                Rep ID: {rep?.rep_id}
              </Text>
            </div>
            <Text size="sm" c="dimmed">
              Consider using the "Deactivate Rep" option instead if you want to
              preserve their data while preventing login access.
            </Text>
            <Group justify="flex-end" gap="sm" mt="md">
              <Button
                variant="outline"
                onClick={() => setIsDeleteModalOpen(false)}
                disabled={isDeleting}
              >
                Cancel
              </Button>
              <Button
                color="red"
                onClick={handleDeleteRep}
                loading={isDeleting}
              >
                {isDeleting ? "Deleting..." : "Delete Permanently"}
              </Button>
            </Group>
          </Stack>
        </Modal>
      </div>
    </PageContent>
  );
}

export default RepDetailsPage;
